
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { 
  Plus, ChevronLeft, ChevronRight, History, Target, AlertCircle, LogIn, 
  UserPlus, Layers, Mail, Lock, Search, Sparkles, ExternalLink, RefreshCw, 
  CheckCircle2, MapPin, Key, Upload, Camera, Trophy, Home, Users, Settings, LogOut,
  Zap, Brain, Calendar, ShieldCheck, User as UserIcon, Heart, Trash2, Edit3, FileText, X, Filter, Ruler, Info, Save, CheckSquare, Square, Baby, Lightbulb, Activity, Clock, Award, Share2, Copy, Link, Eye, Download
} from 'lucide-react';
import Layout from './components/Layout';
import DashboardCard from './components/DashboardCard';
import ProgressChart from './components/ProgressChart';
import StandardsVerificationModal from './components/StandardsVerificationModal';
import { getAchievementLevel, getLevelColor, getLevelDescription, formatTimeFromSeconds } from './utils/motivationalTimes';
import { EVENTS, MOCK_STANDARDS, MOCK_TIMES, MOCK_ATHLETES, MOCK_USERS } from './constants';
import { formatTime, calculatePace, parseTime, getAgeGroup, getAgeGroupAtDate, getNextAgeGroupChange } from './utils/time';
import { LineChart as RechartsLine, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { User, Role, Athlete, TimeEntry, Event, Stroke, QualifyingStandard, WeeklyCheckIn, Course } from './types';

type Screen = 'dashboard' | 'event-detail' | 'roster' | 'focus' | 'admin' | 'add-time' | 'add-athlete' | 'add-event' | 'login' | 'register' | 'manage-swimmer-events' | 'scan-times' | 'shared-view' | 'progress';

interface ExtractedTime {
  swimmerName: string;
  eventName: string;
  distance: number;
  stroke: string;
  timeStr: string;
  place?: number;
  heat?: number;
  lane?: number;
  meetName?: string;
  date?: string;
  selected?: boolean;
  matchedEventName?: string; // The app's event name this matches to
  matchedEventId?: string; // The app's event ID this matches to
  matchConfidence?: 'high' | 'medium' | 'low';
}

interface ResearchResult {
  name: string;
  regionalTimeStr?: string;
  stateTimeStr?: string;
  ageGroup?: string;
  gender?: string;
  course?: Course;
  distance?: number;
  stroke?: Stroke;
  season?: string;
  source?: string;
  validated?: boolean;
  warnings?: string[];
  regionalSeconds?: number;
  stateSeconds?: number;
  verificationScore?: string;
  verificationConfidence?: 'high' | 'medium' | 'low';
}

const STORAGE_KEYS = {
  AUTH: 'sq_prod_session_v3'
};

const getSessionHeaders = (user: User | null) => ({
  'Content-Type': 'application/json',
  ...(user ? { 'X-User-Session': JSON.stringify({ userId: user.id, teamId: user.teamId, role: user.role }) } : {})
});

// Standards an athlete's event is compared against. Honors the athlete's
// trackAgeGroup pin (e.g. a 12yo watching 13-14 cuts): the same real event
// exists in every age group, so we match the twin event by name/course/distance
// and use ITS cuts. Without a pin this is identical to matching by event id.
const getTrackedStandards = (athlete: Athlete | null, event: Event, events: Event[], standards: QualifyingStandard[]): QualifyingStandard[] => {
  if (!athlete || !event) return [];
  const pin = athlete.trackAgeGroup && athlete.trackAgeGroup !== athlete.ageGroup ? athlete.trackAgeGroup : null;
  let target = event;
  if (pin) {
    target = events.find(e =>
      e.name === event.name && e.course === event.course &&
      e.distance === event.distance && e.ageGroup === pin
    ) || event;
  }
  let matched = standards.filter(s => s.eventId === target.id && s.gender === athlete.gender);
  if (matched.length === 0) {
    matched = standards.filter(s => {
      const sEvent = events.find(e => e.id === s.eventId);
      return sEvent && sEvent.name === target.name && sEvent.course === target.course &&
        sEvent.ageGroup === target.ageGroup && s.gender === athlete.gender;
    });
  }
  return matched;
};

const App: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<User | null>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.AUTH);
    return saved ? JSON.parse(saved) : null;
  });
  
  const [users, setUsers] = useState<User[]>(MOCK_USERS);
  const [times, setTimes] = useState<TimeEntry[]>([]);
  const [standards, setStandards] = useState<QualifyingStandard[]>([]);
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dataInitialized, setDataInitialized] = useState(false);

  useEffect(() => { 
    if (currentUser) localStorage.setItem(STORAGE_KEYS.AUTH, JSON.stringify(currentUser));
    else localStorage.removeItem(STORAGE_KEYS.AUTH);
  }, [currentUser]);

  useEffect(() => {
    const loadAllData = async () => {
      if (!currentUser) {
        setIsLoading(false);
        return;
      }

      try {
        await fetch('/api/seed', { method: 'POST', headers: getSessionHeaders(currentUser) });

        const [eventsRes, standardsRes, athletesRes, timesRes, usersRes] = await Promise.all([
          fetch('/api/events', { headers: getSessionHeaders(currentUser) }),
          fetch('/api/standards', { headers: getSessionHeaders(currentUser) }),
          fetch('/api/athletes', { headers: getSessionHeaders(currentUser) }),
          fetch('/api/times', { headers: getSessionHeaders(currentUser) }),
          fetch('/api/auth/users')
        ]);

        if (eventsRes.ok) {
          const dbEvents = await eventsRes.json();
          setEvents(dbEvents.length > 0 ? dbEvents : EVENTS);
        } else {
          setEvents(EVENTS);
        }

        if (standardsRes.ok) {
          const dbStandards = await standardsRes.json();
          setStandards(dbStandards);
        } else {
          setStandards([]);
        }

        if (athletesRes.ok) {
          const dbAthletes = await athletesRes.json();
          setAthletes(dbAthletes);
        } else {
          setAthletes([]);
        }

        if (timesRes.ok) {
          const dbTimes = await timesRes.json();
          setTimes(dbTimes);
        } else {
          setTimes([]);
        }

        if (usersRes.ok) {
          const dbUsers = await usersRes.json();
          const mergedUsers = [...MOCK_USERS];
          for (const dbUser of dbUsers) {
            if (!mergedUsers.some(u => u.email?.toLowerCase() === dbUser.email?.toLowerCase())) {
              mergedUsers.push({
                id: dbUser.id,
                name: dbUser.name,
                email: dbUser.email,
                role: dbUser.role as Role,
                teamId: dbUser.teamId
              });
            }
          }
          setUsers(mergedUsers);
        }

        setDataInitialized(true);
      } catch (err) {
        console.error('Failed to load data:', err);
        setEvents(EVENTS);
        setStandards([]);
        setAthletes([]);
        setTimes([]);
      } finally {
        setIsLoading(false);
      }
    };
    loadAllData();
  }, [currentUser]);

  const [activeTab, setActiveTab] = useState<string>('dashboard');
  const [currentScreen, setCurrentScreen] = useState<Screen>(currentUser ? 'dashboard' : 'login');
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [selectedAthleteId, setSelectedAthleteId] = useState<string | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [registerError, setRegisterError] = useState<string | null>(null);
  
  // Management State
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [editingTimeEntry, setEditingTimeEntry] = useState<TimeEntry | null>(null);
  const [deletingTimeId, setDeletingTimeId] = useState<string | null>(null);
  const [adminMode, setAdminMode] = useState<'search' | 'scan' | 'events' | 'explorer'>('explorer');
  const [explorerFilter, setExplorerFilter] = useState<{ ageGroup: string; gender: 'M' | 'F'; course: Course }>({ ageGroup: '11-12', gender: 'M', course: Course.SCY });

  // AI Insights State
  const [isGeneratingInsights, setIsGeneratingInsights] = useState(false);
  const [strokeInsights, setStrokeInsights] = useState<{[key: string]: string}>({});

  // AI Research & Scan State
  const [isResearching, setIsResearching] = useState(false);
  const [isApplyingResults, setIsApplyingResults] = useState(false);
  const [researchResults, setResearchResults] = useState<ResearchResult[]>([]);
  const [groundingLinks, setGroundingLinks] = useState<{title: string, uri: string}[]>([]);
  const [sourceLinks, setSourceLinks] = useState<{usaSwimming: string, lscWebsite: string | null, lscName: string, pdfSources: string[], webSources: string[]} | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [uploadMimeType, setUploadMimeType] = useState<string>('image/png');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Heat sheet scanning state
  const [heatSheetPreviews, setHeatSheetPreviews] = useState<{data: string, mimeType: string, name: string}[]>([]);
  const [extractedTimes, setExtractedTimes] = useState<ExtractedTime[]>([]);
  const [isExtractingTimes, setIsExtractingTimes] = useState(false);
  const [isImportingTimes, setIsImportingTimes] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState<{current: number, total: number} | null>(null);
  const [scanMeetDetails, setScanMeetDetails] = useState<{ meetName: string; date: string; course: string }>({
    meetName: '',
    date: new Date().toISOString().split('T')[0],
    course: 'SCY'
  });
  const heatSheetInputRef = useRef<HTMLInputElement>(null);

  // Team sharing state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [shareCode, setShareCode] = useState<string | null>(null);
  const [isCreatingShare, setIsCreatingShare] = useState(false);
  const [sharedTeamData, setSharedTeamData] = useState<any>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [isLoadingInvite, setIsLoadingInvite] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [isJoiningTeam, setIsJoiningTeam] = useState(false);
  const [teamJoinError, setTeamJoinError] = useState<string | null>(null);
  const [teamJoinSuccess, setTeamJoinSuccess] = useState<string | null>(null);

  // Standards verification modal state
  const [showVerifyStandards, setShowVerifyStandards] = useState(false);

  // Dashboard & shared view search/filter state
  const [eventSearchQuery, setEventSearchQuery] = useState('');
  const [eventStrokeFilter, setEventStrokeFilter] = useState<string>('all');
  const [eventCourseFilter, setEventCourseFilter] = useState<string>('all');
  const [eventSortBy, setEventSortBy] = useState<string>('qualification');
  const [sharedSearchQuery, setSharedSearchQuery] = useState('');
  
  // Verification state - stores confidence scores per event
  const [verificationResults, setVerificationResults] = useState<Record<string, any>>({});
  const [isVerifying, setIsVerifying] = useState(false);
  
  // Apply confirmation state
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setCurrentScreen(tab as Screen);
    if (tab !== 'admin') {
      setResearchResults([]);
      setGroundingLinks([]);
    }
  };

  // Team sharing functions
  const handleCreateShareLink = async () => {
    if (!currentUser) return;
    setIsCreatingShare(true);
    try {
      const response = await fetch('/api/teams/share', {
        method: 'POST',
        headers: getSessionHeaders(currentUser),
        body: JSON.stringify({ 
          teamId: currentUser.teamId,
          shareName: `${currentUser.name}'s Team`,
          athleteIds: visibleAthletes.map(a => a.id)
        })
      });
      if (response.ok) {
        const data = await response.json();
        const baseUrl = window.location.origin;
        setShareCode(data.shareCode);
        setShareLink(`${baseUrl}?share=${data.shareCode}`);
        setShowShareModal(true);
      }
    } catch (err) {
      console.error('Failed to create share link:', err);
    } finally {
      setIsCreatingShare(false);
    }
  };

  const handleCopyShareLink = () => {
    if (shareLink) {
      navigator.clipboard.writeText(shareLink);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  // Team invite functions
  const handleGetInviteCode = async () => {
    if (!currentUser) return;
    setIsLoadingInvite(true);
    try {
      const response = await fetch('/api/teams/invite', {
        method: 'POST',
        headers: getSessionHeaders(currentUser),
        body: JSON.stringify({})
      });
      if (response.ok) {
        const data = await response.json();
        setInviteCode(data.inviteCode);
      }
    } catch (err) {
      console.error('Failed to get invite code:', err);
    } finally {
      setIsLoadingInvite(false);
    }
  };

  const handleCopyInviteCode = () => {
    if (inviteCode) {
      navigator.clipboard.writeText(inviteCode);
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2000);
    }
  };

  const handleJoinTeam = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!currentUser) return;
    setIsJoiningTeam(true);
    setTeamJoinError(null);
    setTeamJoinSuccess(null);
    try {
      const response = await fetch('/api/teams/join', {
        method: 'POST',
        headers: getSessionHeaders(currentUser),
        body: JSON.stringify({ inviteCode: joinCode.trim().toLowerCase() })
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentUser({ ...currentUser, teamId: data.teamId });
        setInviteCode(null);
        setJoinCode('');
        setTeamJoinSuccess('Joined team! Loading their swimmers and times...');
      } else {
        const err = await response.json();
        setTeamJoinError(err.detail || 'Invalid invite code.');
      }
    } catch (err) {
      setTeamJoinError('Failed to join team. Please try again.');
    } finally {
      setIsJoiningTeam(false);
    }
  };

  // Auto-load the team's invite code when the Team tab opens
  useEffect(() => {
    if (activeTab === 'roster' && currentUser && !inviteCode && !isLoadingInvite) {
      handleGetInviteCode();
    }
  }, [activeTab, currentUser]);

  const loadSharedTeam = async (code: string) => {
    try {
      const response = await fetch(`/api/teams/share/${code}`);
      if (response.ok) {
        const data = await response.json();
        setSharedTeamData(data);
        setCurrentScreen('shared-view');
      } else {
        alert('Share link not found or expired');
      }
    } catch (err) {
      console.error('Failed to load shared team:', err);
    }
  };

  // Check for share code in URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shareCodeParam = params.get('share');
    if (shareCodeParam) {
      loadSharedTeam(shareCodeParam);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadMimeType(file.type);
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadPreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const visibleAthletes = useMemo(() => {
    if (!currentUser) return [];
    if (currentUser.role === Role.ADMIN || currentUser.role === Role.COACH) return athletes;
    if (currentUser.role === Role.PARENT) return athletes.filter(a => a.parentId === currentUser.id);
    if (currentUser.role === Role.SWIMMER) return athletes.filter(a => a.userId === currentUser.id);
    return [];
  }, [currentUser, athletes]);

  const currentAthlete = useMemo(() => {
    if (!currentUser) return null;
    if (selectedAthleteId) {
      const athlete = visibleAthletes.find(a => a.id === selectedAthleteId);
      if (athlete) return athlete;
    }
    return visibleAthletes[0] || null;
  }, [visibleAthletes, selectedAthleteId, currentUser]);

  const getBestTime = (eventId: string, athleteId: string) => {
    const eventTimes = times.filter(t => t.eventId === eventId && t.athleteId === athleteId && !t.isDQ);
    if (eventTimes.length === 0) return undefined;
    return eventTimes.reduce((prev, curr) => (prev.timeSeconds < curr.timeSeconds ? prev : curr));
  };

  const handleToggleEventSelection = async (eventId: string) => {
    if (!currentAthlete || !currentUser) return;
    const isSelected = currentAthlete.selectedEventIds.includes(eventId);
    const newSelected = isSelected 
      ? currentAthlete.selectedEventIds.filter(id => id !== eventId)
      : [...currentAthlete.selectedEventIds, eventId];

    setAthletes(prev => prev.map(a => a.id === currentAthlete.id ? { ...a, selectedEventIds: newSelected } : a));
    
    try {
      await fetch(`/api/athletes/${currentAthlete.id}`, {
        method: 'PUT',
        headers: getSessionHeaders(currentUser),
        body: JSON.stringify({ ...currentAthlete, selectedEventIds: newSelected })
      });
    } catch (err) {
      console.error('Failed to update athlete events:', err);
    }
  };

  // Pin which age group's cuts this athlete compares against ('' = auto/current group)
  const handleSetTrackGroup = async (group: string) => {
    if (!currentAthlete || !currentUser) return;
    const pinned = group === 'auto' ? undefined : group;
    setAthletes(prev => prev.map(a => a.id === currentAthlete.id ? { ...a, trackAgeGroup: pinned } : a));
    try {
      const res = await fetch(`/api/athletes/${currentAthlete.id}`, {
        method: 'PUT',
        headers: getSessionHeaders(currentUser),
        body: JSON.stringify({ ...currentAthlete, trackAgeGroup: group === 'auto' ? '' : group })
      });
      if (res.ok) {
        const updated = await res.json();
        setAthletes(prev => prev.map(a => a.id === updated.id ? { ...a, ...updated } : a));
      }
    } catch (err) {
      console.error('Failed to update track group:', err);
    }
  };

  const handleGenerateStrokeInsights = async () => {
    if (!currentAthlete) return;
    setIsGeneratingInsights(true);
    try {
      const athleteTimes = times.filter(t => t.athleteId === currentAthlete.id);
      const timeData = athleteTimes.map(t => {
        const ev = events.find(e => e.id === t.eventId);
        return {
          stroke: ev?.stroke,
          distance: ev?.distance,
          time: formatTime(t.timeSeconds),
          date: t.date,
          ageGroup: t.ageGroupAtTime
        };
      });

      const response = await fetch('/api/ai/stroke-insights', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-User-Session': JSON.stringify(currentUser)
        },
        body: JSON.stringify({
          athleteName: currentAthlete.name,
          ageGroup: currentAthlete.ageGroup,
          gender: currentAthlete.gender,
          timeData
        })
      });

      if (!response.ok) throw new Error('Failed to generate insights');
      
      const data = await response.json();
      if (data.insights) {
        setStrokeInsights(data.insights);
      }
    } catch (err) {
      console.error(err);
      alert("Failed to generate stroke insights.");
    } finally {
      setIsGeneratingInsights(false);
    }
  };

  const handleAnalyzeDocument = async () => {
    if (!uploadPreview) return;
    setIsResearching(true);
    setResearchResults([]);
    setGroundingLinks([]);
    try {
      const base64Data = uploadPreview.split(',')[1];
      
      const response = await fetch('/api/ai/analyze-document', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-User-Session': JSON.stringify(currentUser)
        },
        body: JSON.stringify({
          imageData: base64Data,
          mimeType: uploadMimeType || 'image/png'
        })
      });

      if (!response.ok) throw new Error('Analysis failed');
      
      const data = await response.json();
      if (data.results && data.results.length > 0) {
        setResearchResults(data.results);
      } else {
        alert(data.error || "No standards found in document. Please ensure the text is clear.");
      }
    } catch (err: any) {
      console.error(err);
      alert("Analysis failed. Try a clearer document or photo.");
    } finally {
      setIsResearching(false);
    }
  };

  const handleResearchStandards = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    setIsResearching(true);
    setResearchResults([]);
    setGroundingLinks([]);
    try {
      // Collect unique event names from the swimmer's current events to include in research
      const searchCourse = formData.get('course') as string;
      const searchAgeGroup = formData.get('ageGroup') as string;
      const swimmerEventNames = [...new Set(
        events
          .filter(ev => ev.course === searchCourse || ev.ageGroup === searchAgeGroup)
          .map(ev => ev.name)
      )];

      const response = await fetch('/api/ai/research-standards', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-User-Session': JSON.stringify(currentUser)
        },
        body: JSON.stringify({
          ageGroup: formData.get('ageGroup'),
          gender: formData.get('gender'),
          stateLocation: formData.get('stateLocation'),
          course: formData.get('course'),
          season: formData.get('season'),
          customEvents: swimmerEventNames.length > 0 ? swimmerEventNames : undefined
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `Search failed (${response.status})`);
      }
      
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        // Ensure results have ageGroup and gender from the search
        const enrichedResults = data.results.map((r: ResearchResult) => ({
          ...r,
          ageGroup: r.ageGroup || formData.get('ageGroup'),
          gender: r.gender || formData.get('gender'),
          course: r.course || formData.get('course'),
          season: data.season || formData.get('season')
        }));
        setResearchResults(enrichedResults);
      } else {
        alert(data.error || "Could not extract specific times. Please check sources or try a different location.");
      }

      if (data.citations && data.citations.length > 0) {
        setGroundingLinks(data.citations);
      }
      if (data.sourceLinks) {
        setSourceLinks(data.sourceLinks);
      }
    } catch (err: any) {
      console.error(err);
      alert(err.message || "Search failed. Please try again.");
    } finally {
      setIsResearching(false);
    }
  };

  const syncSingleResult = async (res: ResearchResult) => {
    if (!res.name || !currentUser) return;

    const normalizeCourse = (c: any): Course => {
      if (c === Course.SCY || c === Course.SCM || c === Course.LCM) return c;
      if (typeof c === 'string') {
        const lower = c.toLowerCase();
        if (lower === 'lcm' || lower.includes('long course')) return Course.LCM;
        if (lower === 'scm' || lower.includes('short course meters')) return Course.SCM;
        if (lower.includes('meter')) return Course.LCM; // Default meters to LCM
      }
      return Course.SCY; // Default to SCY
    };

    const normalizeStroke = (s: string) => {
      const l = (s || '').toLowerCase();
      if (l.includes('back')) return 'Back';
      if (l.includes('breast')) return 'Breast';
      if (l.includes('fly') || l.includes('butter')) return 'Fly';
      if (l.includes('im') || l.includes('medley')) return 'IM';
      return 'Free';
    };
    const normalizeAgeGroup = (a: string) => {
      const s = (a || '').trim().toLowerCase();
      if (/(8|10)\s*&?\s*(u|under)/.test(s) || /^(8|10)$/.test(s)) return '10U';
      if (/1[12]\s*[-&]\s*1[12]/.test(s) || /12\s*&?\s*(u|under)/.test(s)) return '11-12';
      if (/1[34]\s*[-&]\s*1[34]/.test(s) || /13\s*&?\s*(u|under)/.test(s)) return '13-14';
      if (/1[56]\s*[-&]\s*1[56]/.test(s)) return '15-16';
      if (/1[78]\s*[-&]\s*1[78]/.test(s)) return '17-18';
      return a;
    };

    const normalizedCourse = normalizeCourse(res.course);
    const genderVal: 'M' | 'F' = (res.gender === 'Women' || res.gender === 'F' || res.gender === 'Girls' ? 'F' : 'M');
    const ageVal = normalizeAgeGroup(res.ageGroup || currentAthlete?.ageGroup || '11-12');
    const seasonVal = res.season || '2026';

    // Find or create event specific to this age group — match by canonical identity, not raw name
    let existingEvent = events.find(e => 
      e.distance === (res.distance || 50) &&
      normalizeStroke(e.stroke) === normalizeStroke(res.stroke || res.name) &&
      e.course === normalizedCourse &&
      e.ageGroup === ageVal
    );

    let eventId: string;

    if (existingEvent) {
      eventId = existingEvent.id;
    } else {
      // Create event specific to this age group - this ensures swimmers only see events for their age bracket
      const newEvent: Event = {
        id: `e-ai-${ageVal}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: res.name,
        distance: res.distance || 50,
        stroke: res.stroke || Stroke.FREE,
        course: normalizedCourse,
        ageGroup: ageVal
      };
      
      try {
        const response = await fetch('/api/events', {
          method: 'POST',
          headers: getSessionHeaders(currentUser),
          body: JSON.stringify(newEvent)
        });
        if (response.ok) {
          const savedEvent = await response.json();
          setEvents(prev => [...prev, savedEvent]);
          eventId = savedEvent.id;
        } else {
          setEvents(prev => [...prev, newEvent]);
          eventId = newEvent.id;
        }
      } catch (err) {
        setEvents(prev => [...prev, newEvent]);
        eventId = newEvent.id;
      }
    }

    const newStandards: QualifyingStandard[] = [];

    if (res.regionalTimeStr) {
      newStandards.push({
        id: `s-reg-${Date.now()}-${Math.random()}`,
        eventId, region: 'Regional', ageGroup: ageVal, gender: genderVal, course: normalizedCourse,
        cutTimeSeconds: parseTime(res.regionalTimeStr), season: seasonVal,
        verificationScore: res.verificationScore || undefined,
        verificationConfidence: res.verificationConfidence || undefined
      });
    }

    if (res.stateTimeStr) {
      newStandards.push({
        id: `s-state-${Date.now()}-${Math.random()}`,
        eventId, region: 'State', ageGroup: ageVal, gender: genderVal, course: normalizedCourse,
        cutTimeSeconds: parseTime(res.stateTimeStr), season: seasonVal,
        verificationScore: res.verificationScore || undefined,
        verificationConfidence: res.verificationConfidence || undefined
      });
    }

    if (newStandards.length > 0) {
      try {
        const response = await fetch('/api/standards/bulk', {
          method: 'POST',
          headers: getSessionHeaders(currentUser),
          body: JSON.stringify({ standards: newStandards })
        });
        if (response.ok) {
          const savedStandards = await response.json();
          setStandards(prev => {
            const filtered = prev.filter(s => 
              !(s.eventId === eventId && s.gender === genderVal && s.ageGroup === ageVal && s.course === normalizedCourse)
            );
            return [...filtered, ...savedStandards];
          });
        } else {
          setStandards(prev => {
            const filtered = prev.filter(s => 
              !(s.eventId === eventId && s.gender === genderVal && s.ageGroup === ageVal && s.course === normalizedCourse)
            );
            return [...filtered, ...newStandards];
          });
        }
      } catch (err) {
        setStandards(prev => {
          const filtered = prev.filter(s => 
            !(s.eventId === eventId && s.gender === genderVal && s.ageGroup === ageVal && s.course === normalizedCourse)
          );
          return [...filtered, ...newStandards];
        });
      }
    }
  };

  const handleApplyResults = async () => {
    setIsApplyingResults(true);
    let applied = 0;
    try {
      for (const res of researchResults) {
        await syncSingleResult(res);
        applied++;
      }
      setResearchResults([]);
      setGroundingLinks([]);
      setUploadPreview(null);
      alert(`Successfully applied ${applied} qualifying standards!`);
    } catch (err) {
      console.error('Error applying results:', err);
      alert(`Applied ${applied} standards. Some may have failed.`);
    } finally {
      setIsApplyingResults(false);
    }
  };

  const handleLoginSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoginError(null);
    const formData = new FormData(e.currentTarget);
    const email = (formData.get('email') as string).toLowerCase().trim();
    const password = formData.get('password') as string;
    
    // First try mock users for demo accounts
    const mockUser = MOCK_USERS.find(u => u.email?.toLowerCase() === email && u.password === password);
    if (mockUser) {
      setCurrentUser(mockUser);
      handleTabChange(mockUser.role === Role.COACH || mockUser.role === Role.ADMIN ? 'roster' : 'dashboard');
      return;
    }

    // Try database login
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      if (response.ok) {
        const user = await response.json();
        const userWithRole: User = {
          id: user.id,
          name: user.name,
          email: user.email,
          role: user.role as Role,
          teamId: user.teamId
        };
        setCurrentUser(userWithRole);
        handleTabChange(userWithRole.role === Role.COACH || userWithRole.role === Role.ADMIN ? 'roster' : 'dashboard');
      } else {
        const error = await response.json();
        setLoginError(error.detail || error.error || 'Invalid credentials.');
      }
    } catch (err) {
      setLoginError('Login failed. Please try again.');
    }
  };

  const quickLogin = (email: string) => {
    const user = users.find(u => u.email?.toLowerCase() === email.toLowerCase());
    if (user) {
      setCurrentUser(user);
      handleTabChange(user.role === Role.COACH || user.role === Role.ADMIN ? 'roster' : 'dashboard');
    }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setCurrentScreen('login');
    setActiveTab('dashboard');
  };

  const handleRegisterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setRegisterError(null);
    const formData = new FormData(e.currentTarget);
    const name = (formData.get('name') as string).trim();
    const email = (formData.get('email') as string).toLowerCase().trim();
    const password = formData.get('password') as string;
    const role = formData.get('role') as Role;
    const inviteCode = ((formData.get('inviteCode') as string) || '').trim().toLowerCase();

    // Check mock users first
    if (MOCK_USERS.some(u => u.email?.toLowerCase() === email)) {
      setRegisterError('An account with this email already exists.');
      return;
    }

    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password, role, ...(inviteCode ? { inviteCode } : {}) })
      });

      if (response.ok) {
        const user = await response.json();
        const newUser: User = {
          id: user.id,
          name: user.name,
          email: user.email,
          role: user.role as Role,
          teamId: user.teamId
        };
        setUsers(prev => [...prev, newUser]);
        setCurrentUser(newUser);
        handleTabChange(role === Role.COACH ? 'roster' : 'dashboard');
      } else {
        const error = await response.json();
        setRegisterError(error.detail || error.error || 'Registration failed.');
      }
    } catch (err) {
      setRegisterError('Registration failed. Please try again.');
    }
  };

  const handleUpdateEvent = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingEvent) return;
    const formData = new FormData(e.currentTarget);
    const ageGroup = formData.get('ageGroup') as string;
    const course = formData.get('course') as Course;

    // Standard updates for both genders
    const regCutM = formData.get('regCutM') as string;
    const stateCutM = formData.get('stateCutM') as string;
    const regCutF = formData.get('regCutF') as string;
    const stateCutF = formData.get('stateCutF') as string;

    setEvents(prev => prev.map(ev => ev.id === editingEvent.id ? { 
      ...ev, 
      name: formData.get('name') as string, 
      distance: parseInt(formData.get('distance') as string), 
      stroke: formData.get('stroke') as Stroke, 
      course, 
      ageGroup 
    } : ev));

    setStandards(prev => {
      // Clear standards for this specific demographic to ensure they are replaced correctly
      let nextStandards = prev.filter(s => !(s.eventId === editingEvent.id && s.ageGroup === ageGroup && s.course === course));

      const addStd = (gender: 'M' | 'F', region: 'Regional' | 'State', time: string) => {
        if (time) nextStandards.push({ id: `s-${gender}-${region}-${Date.now()}`, eventId: editingEvent.id, region, ageGroup, gender, course, cutTimeSeconds: parseTime(time), season: '2025' });
      };

      addStd('M', 'Regional', regCutM);
      addStd('M', 'State', stateCutM);
      addStd('F', 'Regional', regCutF);
      addStd('F', 'State', stateCutF);

      return nextStandards;
    });

    setEditingEvent(null);
    alert("Global standards updated for both Boys and Girls.");
  };

  const handleAddAthlete = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!currentUser) return;
    const formData = new FormData(e.currentTarget);
    const dob = formData.get('dob') as string;
    const newAthlete: Athlete = {
      id: `a${Date.now()}`,
      parentId: currentUser.role === Role.PARENT ? currentUser.id : undefined,
      name: formData.get('name') as string,
      dob,
      gender: formData.get('gender') as 'M' | 'F',
      ageGroup: getAgeGroupAtDate(dob, new Date().toISOString()),
      selectedEventIds: []
    };
    
    try {
      const response = await fetch('/api/athletes', {
        method: 'POST',
        headers: getSessionHeaders(currentUser),
        body: JSON.stringify(newAthlete)
      });
      if (response.ok) {
        const savedAthlete = await response.json();
        setAthletes(prev => [...prev, savedAthlete]);
        setCurrentScreen('roster');
      } else {
        alert('Failed to save athlete. Please try again.');
      }
    } catch (err) {
      console.error('Failed to save athlete:', err);
      alert('Failed to save athlete. Please try again.');
    }
  };

  const handleAddEvent = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const newEvent: Event = {
      id: `e${Date.now()}`,
      name: formData.get('name') as string,
      distance: parseInt(formData.get('distance') as string),
      stroke: formData.get('stroke') as Stroke,
      course: formData.get('course') as Course,
      ageGroup: formData.get('ageGroup') as string
    };
    
    try {
      const response = await fetch('/api/events', {
        method: 'POST',
        headers: getSessionHeaders(currentUser),
        body: JSON.stringify(newEvent)
      });
      if (response.ok) {
        const savedEvent = await response.json();
        setEvents(prev => [...prev, savedEvent]);
        e.currentTarget.reset();
        alert("Event created. Use Explorer or Edit to add cut times.");
      } else {
        alert("Failed to save event. Please try again.");
      }
    } catch (err) {
      console.error('Failed to save event:', err);
      alert("Failed to save event. Please try again.");
    }
  };

  const handleAddTime = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedEventId || !currentAthlete || !currentUser) return;
    const formData = new FormData(e.currentTarget);
    const timeStr = formData.get('time') as string;
    const dateStr = formData.get('date') as string || new Date().toISOString().split('T')[0];
    const meetNameStr = formData.get('meetName') as string || '';
    const isDQ = formData.get('isDQ') === 'on';
    
    const newTime: TimeEntry = {
      id: `t${Date.now()}`,
      athleteId: currentAthlete.id,
      eventId: selectedEventId,
      timeSeconds: isDQ ? 0 : parseTime(timeStr),
      date: dateStr,
      meetName: meetNameStr || undefined,
      ageGroupAtTime: currentAthlete.ageGroup,
      course: events.find(e => e.id === selectedEventId)?.course || Course.SCY,
      isDQ
    };
    
    try {
      const response = await fetch('/api/times', {
        method: 'POST',
        headers: getSessionHeaders(currentUser),
        body: JSON.stringify(newTime)
      });
      if (response.ok) {
        const savedTime = await response.json();
        setTimes(prev => [...prev, savedTime]);
        setCurrentScreen('event-detail');
      } else {
        alert("Failed to save time. Please try again.");
      }
    } catch (err) {
      console.error('Failed to save time:', err);
      alert("Failed to save time. Please try again.");
    }
  };

  const handleUpdateTime = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingTimeEntry || !currentUser) return;
    const formData = new FormData(e.currentTarget);
    const timeStr = formData.get('time') as string;
    const dateStr = formData.get('date') as string || editingTimeEntry.date;
    const meetNameStr = formData.get('meetName') as string || '';
    const isDQ = formData.get('isDQ') === 'on';

    const updatedTime = {
      ...editingTimeEntry,
      timeSeconds: isDQ ? 0 : parseTime(timeStr),
      date: dateStr,
      meetName: meetNameStr || undefined,
      isDQ
    };

    try {
      const response = await fetch(`/api/times/${editingTimeEntry.id}`, {
        method: 'PUT',
        headers: getSessionHeaders(currentUser),
        body: JSON.stringify(updatedTime)
      });
      if (response.ok) {
        const savedTime = await response.json();
        setTimes(prev => prev.map(t => t.id === savedTime.id ? savedTime : t));
        setEditingTimeEntry(null);
      } else {
        alert("Failed to update time. Please try again.");
      }
    } catch (err) {
      console.error('Failed to update time:', err);
      alert("Failed to update time. Please try again.");
    }
  };

  const handleEditStandard = async (standardId: string, newTime: number) => {
    try {
      const existing = standards.find(s => s.id === standardId);
      if (!existing) return;
      // Delete old and create new with updated time
      await fetch(`/api/standards/${standardId}`, { method: 'DELETE', headers: getSessionHeaders(currentUser) });
      const updated = { ...existing, cutTimeSeconds: newTime, id: `s_${Date.now()}` };
      const res = await fetch('/api/standards', {
        method: 'POST',
        headers: getSessionHeaders(currentUser),
        body: JSON.stringify(updated)
      });
      if (res.ok) {
        setStandards(prev => prev.map(s => s.id === standardId ? { ...updated } : s));
      }
    } catch (err) {
      console.error('Failed to edit standard:', err);
    }
  };

  const handleCreateStandard = async (eventId: string, ageGroup: string, gender: 'M' | 'F', course: string, region: 'Regional' | 'State', cutTimeSeconds: number) => {
    try {
      const newStandard = {
        id: `s_${Date.now()}_${region.toLowerCase()}`,
        eventId,
        region,
        ageGroup,
        gender,
        course,
        cutTimeSeconds,
        season: '2025'
      };
      const res = await fetch('/api/standards', {
        method: 'POST',
        headers: getSessionHeaders(currentUser),
        body: JSON.stringify(newStandard)
      });
      if (res.ok) {
        setStandards(prev => [...prev, newStandard as any]);
      }
    } catch (err) {
      console.error('Failed to create standard:', err);
    }
  };


  const handleVerifyTimes = async (stateLocation?: string) => {
    if (!currentAthlete || !currentUser) return;
    setIsVerifying(true);
    
    try {
      // Gather all events with standards for this swimmer
      const swimmerEvents = events.filter(e => currentAthlete.selectedEventIds.includes(e.id));
      const timesToVerify = swimmerEvents.map(e => {
        const eventStandards = standards.filter(s => s.eventId === e.id && s.gender === currentAthlete.gender && s.ageGroup === e.ageGroup);
        const regional = eventStandards.find(s => s.region === 'Regional');
        const state = eventStandards.find(s => s.region === 'State');
        return {
          name: e.name,
          eventId: e.id,
          course: e.course,
          ageGroup: e.ageGroup,
          gender: currentAthlete.gender,
          regionalTimeStr: regional ? formatTime(regional.cutTimeSeconds) : '',
          stateTimeStr: state ? formatTime(state.cutTimeSeconds) : ''
        };
      }).filter(t => t.regionalTimeStr || t.stateTimeStr);
      
      if (timesToVerify.length === 0) {
        alert('No cut times to verify. Research standards first.');
        setIsVerifying(false);
        return;
      }
      
      const response = await fetch('/api/ai/verify-times', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Session': JSON.stringify(currentUser) },
        body: JSON.stringify({
          times: timesToVerify,
          stateLocation: stateLocation || 'USA Swimming',
          season: '2025'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        const resultMap: Record<string, any> = {};
        for (const r of data.results) {
          resultMap[r.eventId] = r;
        }
        setVerificationResults(resultMap);
      }
    } catch (err) {
      console.error('Verification failed:', err);
    } finally {
      setIsVerifying(false);
    }
  };



  const handleDeleteTime = async (timeId: string) => {
    setDeletingTimeId(timeId);
  };

  const confirmDeleteTime = async () => {
    if (!deletingTimeId) return;
    
    try {
      const response = await fetch(`/api/times/${deletingTimeId}`, {
        method: 'DELETE',
        headers: { 
          'Content-Type': 'application/json',
          'X-User-Session': JSON.stringify(currentUser)
        }
      });
      
      if (response.ok) {
        setTimes(prev => prev.filter(t => t.id !== deletingTimeId));
        setEditingTimeEntry(null);
        setDeletingTimeId(null);
      } else {
        alert('Failed to delete time. Please try again.');
        setDeletingTimeId(null);
      }
    } catch (err) {
      console.error('Failed to delete time:', err);
      alert('Failed to delete time. Please try again.');
      setDeletingTimeId(null);
    }
  };

  // Heat sheet scanning handlers
  const handleHeatSheetUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const newPreviews: {data: string, mimeType: string, name: string}[] = [];
    let loaded = 0;
    
    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        newPreviews.push({
          data: event.target?.result as string,
          mimeType: file.type,
          name: file.name
        });
        loaded++;
        if (loaded === files.length) {
          setHeatSheetPreviews(prev => [...prev, ...newPreviews]);
          setExtractedTimes([]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removeHeatSheetImage = (index: number) => {
    setHeatSheetPreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Helper to match extracted event to app events
  const matchExtractedEventToApp = (extractedTime: ExtractedTime): { matchedName: string; confidence: 'high' | 'medium' | 'low'; matchedEventId?: string } => {
    // Comprehensive stroke name normalization
    const strokeMap: Record<string, string> = {
      'Free': 'Freestyle', 'Freestyle': 'Freestyle', 'FR': 'Freestyle', 'Fr': 'Freestyle',
      'free': 'Freestyle', 'freestyle': 'Freestyle', 'FREESTYLE': 'Freestyle', 'Frstyl': 'Freestyle',
      'Back': 'Backstroke', 'Backstroke': 'Backstroke', 'BK': 'Backstroke', 'Bk': 'Backstroke',
      'back': 'Backstroke', 'backstroke': 'Backstroke', 'BACKSTROKE': 'Backstroke', 'Bckstrk': 'Backstroke',
      'Breast': 'Breaststroke', 'Breaststroke': 'Breaststroke', 'BR': 'Breaststroke', 'Br': 'Breaststroke',
      'breast': 'Breaststroke', 'breaststroke': 'Breaststroke', 'BREASTSTROKE': 'Breaststroke', 'Brstrk': 'Breaststroke',
      'Fly': 'Butterfly', 'Butterfly': 'Butterfly', 'FL': 'Butterfly', 'Fl': 'Butterfly',
      'fly': 'Butterfly', 'butterfly': 'Butterfly', 'BUTTERFLY': 'Butterfly',
      'IM': 'Individual Medley', 'Individual Medley': 'Individual Medley', 'I.M.': 'Individual Medley',
      'Ind. Medley': 'Individual Medley', 'Ind Medley': 'Individual Medley', 'Medley': 'Individual Medley',
      'IND MEDLEY': 'Individual Medley', 'I.M': 'Individual Medley'
    };
    
    const normalizedStroke = strokeMap[extractedTime.stroke] || extractedTime.stroke;
    
    // Also try to parse the full event name for matching (e.g. "100 M Freestyle", "100 Yard Free")
    const eventNameStr = (extractedTime.eventName || '').toLowerCase();
    let parsedDistance = extractedTime.distance;
    let parsedStroke = normalizedStroke;
    
    // Parse event names like "100 M Freestyle", "200 Yard Breast", "50 Free", "100 Fly"
    const nameMatch = eventNameStr.match(/(\d+)\s*(?:m|meter|meters|yard|yards|y)?\s*(.+)/i);
    if (nameMatch) {
      parsedDistance = parseInt(nameMatch[1]);
      const strokePart = nameMatch[2].trim().replace(/^(m|meter|meters|yard|yards|y)\s*/i, '');
      // Try to map the stroke part
      for (const [key, val] of Object.entries(strokeMap)) {
        if (strokePart.toLowerCase() === key.toLowerCase() || strokePart.toLowerCase().startsWith(key.toLowerCase())) {
          parsedStroke = val;
          break;
        }
      }
    }
    
    // Try to find exact match by distance + stroke (using both original and parsed values)
    const exactMatch = events.find(e => 
      e.distance === parsedDistance && 
      e.stroke === parsedStroke
    ) || events.find(e => 
      e.distance === extractedTime.distance && 
      e.stroke === normalizedStroke
    );
    
    if (exactMatch) {
      return { matchedName: exactMatch.name, confidence: 'high', matchedEventId: exactMatch.id };
    }
    
    // Try fuzzy match - distance only
    const distanceMatch = events.find(e => e.distance === parsedDistance || e.distance === extractedTime.distance);
    if (distanceMatch) {
      return { matchedName: `${parsedDistance} ${parsedStroke.split(' ')[0]}`, confidence: 'medium' };
    }
    
    return { matchedName: extractedTime.eventName, confidence: 'low' };
  };

  const handleExtractTimes = async () => {
    if (heatSheetPreviews.length === 0 || !currentAthlete) return;
    
    setIsExtractingTimes(true);
    setExtractionProgress({ current: 0, total: heatSheetPreviews.length });
    const allExtractedTimes: ExtractedTime[] = [];
    
    try {
      for (let i = 0; i < heatSheetPreviews.length; i++) {
        setExtractionProgress({ current: i + 1, total: heatSheetPreviews.length });
        const preview = heatSheetPreviews[i];
        const base64Data = preview.data.split(',')[1];
        
        const response = await fetch('/api/ai/extract-heat-times', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-User-Session': JSON.stringify(currentUser)
          },
          body: JSON.stringify({
            imageData: base64Data,
            mimeType: preview.mimeType,
            swimmerName: currentAthlete.name
          })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.times) {
            // Process each time and match to app events
            const timesWithMatching = data.times.map((t: ExtractedTime) => {
              const match = matchExtractedEventToApp(t);
              return { 
                ...t, 
                selected: t.swimmerName?.toLowerCase().includes(currentAthlete.name.toLowerCase().split(' ')[0]) || false,
                matchedEventName: match.matchedName,
                matchedEventId: match.matchedEventId,
                matchConfidence: match.confidence
              };
            });
            allExtractedTimes.push(...timesWithMatching);
          }
        }
      }
      
      if (allExtractedTimes.length > 0) {
        setExtractedTimes(allExtractedTimes);
      } else {
        alert('No times found in the uploaded images');
      }
    } catch (err) {
      console.error('Failed to extract times:', err);
      alert('Failed to extract times from images. Please try again.');
    } finally {
      setIsExtractingTimes(false);
      setExtractionProgress(null);
    }
  };

  const handleImportExtractedTimes = async () => {
    if (!currentAthlete || !currentUser) return;
    
    const selectedTimes = extractedTimes.filter(t => t.selected);
    if (selectedTimes.length === 0) {
      alert('Please select at least one time to import');
      return;
    }

    if (!scanMeetDetails.meetName.trim()) {
      alert('Please enter a meet name');
      return;
    }

    setIsImportingTimes(true);
    let imported = 0;
    const newEventIds: string[] = [];

    try {
      for (const time of selectedTimes) {
        // Find or create matching event
        const strokeMap: Record<string, string> = {
          'Free': 'Freestyle', 'Freestyle': 'Freestyle',
          'Back': 'Backstroke', 'Backstroke': 'Backstroke',
          'Breast': 'Breaststroke', 'Breaststroke': 'Breaststroke',
          'Fly': 'Butterfly', 'Butterfly': 'Butterfly',
          'IM': 'Individual Medley', 'Individual Medley': 'Individual Medley'
        };
        const normalizedStroke = strokeMap[time.stroke] || time.stroke;
        
        // If user manually mapped to an existing event, use that directly
        let matchingEvent = time.matchedEventId 
          ? events.find(e => e.id === time.matchedEventId) 
          : null;
        
        // Otherwise try auto-matching by distance + stroke
        if (!matchingEvent) {
          matchingEvent = events.find(e => 
            e.distance === time.distance && 
            e.stroke === normalizedStroke &&
            e.ageGroup === currentAthlete.ageGroup &&
            e.course === scanMeetDetails.course
          );
        }

        if (!matchingEvent) {
          // Create the event
          const newEvent = {
            id: `e_scan_${Date.now()}_${imported}`,
            name: time.eventName || `${time.distance} ${normalizedStroke.split(' ')[0]}`,
            distance: time.distance,
            stroke: normalizedStroke,
            course: scanMeetDetails.course,
            ageGroup: currentAthlete.ageGroup
          };
          
          const eventRes = await fetch('/api/events', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-User-Session': JSON.stringify(currentUser) },
            body: JSON.stringify(newEvent)
          });
          if (eventRes.ok) {
            matchingEvent = await eventRes.json();
            setEvents(prev => [...prev, matchingEvent!]);
          }
        }

        if (matchingEvent) {
          // Track new event IDs to add to athlete
          if (!currentAthlete.selectedEventIds.includes(matchingEvent.id) && !newEventIds.includes(matchingEvent.id)) {
            newEventIds.push(matchingEvent.id);
          }

          // Parse time string to seconds
          const parseTimeStr = (str: string): number => {
            const cleaned = str.replace(/[^\d:.]/g, '');
            const parts = cleaned.split(':');
            if (parts.length === 2) {
              return parseFloat(parts[0]) * 60 + parseFloat(parts[1]);
            }
            return parseFloat(cleaned);
          };

          const newTime = {
            athleteId: currentAthlete.id,
            eventId: matchingEvent.id,
            timeSeconds: parseTimeStr(time.timeStr),
            course: scanMeetDetails.course,
            date: scanMeetDetails.date,
            meetName: scanMeetDetails.meetName,
            ageGroupAtTime: currentAthlete.ageGroup,
            isDQ: false
          };

          const timeRes = await fetch('/api/times', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-User-Session': JSON.stringify(currentUser) },
            body: JSON.stringify(newTime)
          });
          if (timeRes.ok) {
            const savedTime = await timeRes.json();
            setTimes(prev => [...prev, savedTime]);
            imported++;
          }
        }
      }

      // Update athlete's selected events if we added new events
      if (newEventIds.length > 0 && imported > 0) {
        const updatedEventIds = [...currentAthlete.selectedEventIds, ...newEventIds];
        const athleteRes = await fetch(`/api/athletes/${currentAthlete.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'X-User-Session': JSON.stringify(currentUser) },
          body: JSON.stringify({ ...currentAthlete, selectedEventIds: updatedEventIds })
        });
        if (athleteRes.ok) {
          const updatedAthlete = await athleteRes.json();
          setAthletes(prev => prev.map(a => a.id === updatedAthlete.id ? updatedAthlete : a));
        }
      }

      if (imported > 0) {
        setHeatSheetPreviews([]);
        setExtractedTimes([]);
        setScanMeetDetails({ meetName: '', date: new Date().toISOString().split('T')[0], course: 'SCY' });
        setCurrentScreen('dashboard');
        // Small delay to let state update
        setTimeout(() => {
          alert(`Successfully imported ${imported} time(s)!`);
        }, 100);
      } else {
        alert('Failed to import times. Please try again.');
      }
    } catch (err) {
      console.error('Failed to import times:', err);
      alert('An error occurred while importing times.');
    } finally {
      setIsImportingTimes(false);
    }
  };

  const toggleExtractedTime = (index: number) => {
    setExtractedTimes(prev => prev.map((t, i) => 
      i === index ? { ...t, selected: !t.selected } : t
    ));
  };

  // Stroke guide data for parents and fans with video links
  const strokeGuide: Record<string, { description: string; whatToWatch: string[]; keyMoments: string[]; videos: { title: string; url: string; source: string }[] }> = {
    'Freestyle': {
      description: 'The fastest and most common stroke. Swimmers alternate arm strokes while flutter kicking, breathing to the side.',
      whatToWatch: [
        'Smooth, rhythmic arm turnover - arms should enter the water fingertips first',
        'Head position - should be looking down, not forward',
        'Kick should be fast and compact, not big splashy kicks',
        'Watch for bilateral breathing (both sides) in longer events',
        'Streamline off the walls - tight body position underwater'
      ],
      keyMoments: ['Start/dive entry', 'Flip turns at walls', 'Final sprint to the finish', 'Breathing pattern'],
      videos: [
        { title: 'Freestyle Technique - Full Stroke', url: 'https://www.youtube.com/watch?v=5HLW2AI1Ink', source: 'USA Swimming' },
        { title: 'How to Swim Freestyle', url: 'https://www.youtube.com/watch?v=wMPIERLNu_s', source: 'MySwimPro' },
        { title: 'Freestyle Flip Turn Tutorial', url: 'https://www.youtube.com/watch?v=hKxjRUMl8_I', source: 'Skills NT' }
      ]
    },
    'Backstroke': {
      description: 'The only stroke swum on the back. Swimmers alternate arms while flutter kicking, starting in the water.',
      whatToWatch: [
        'Rotation - body should rock side to side with each stroke',
        'Arms should enter pinky-first, straight above the shoulder',
        'Head stays still, looking straight up',
        'Consistent kick tempo throughout',
        'Backstroke flags (5 meters out) help swimmers know when to flip'
      ],
      keyMoments: ['Backstroke start (in water, pushing off wall)', 'Backstroke flip turn', 'Finish - must touch on back'],
      videos: [
        { title: 'Backstroke Technique', url: 'https://www.youtube.com/watch?v=pFkCXgBqNJc', source: 'USA Swimming' },
        { title: 'How to Swim Backstroke', url: 'https://www.youtube.com/watch?v=cZvdUWrGmQg', source: 'MySwimPro' },
        { title: 'Backstroke Start & Turn', url: 'https://www.youtube.com/watch?v=jJdZ1X8Fy3w', source: 'Skills NT' }
      ]
    },
    'Breaststroke': {
      description: 'A slower, technical stroke with a frog-like kick. Arms and legs move symmetrically.',
      whatToWatch: [
        'Timing is crucial - pull, breathe, kick, glide sequence',
        'Kick should be a powerful whip kick, not a scissor kick',
        'Head comes forward to breathe, not up',
        'Arms must stay in front of the shoulders (no pulling past the waist)',
        'Underwater pullout after each wall - one pull, one kick allowed'
      ],
      keyMoments: ['Underwater pullout (huge time saver)', 'Two-hand touch at walls and finish', 'Kick timing and power'],
      videos: [
        { title: 'Breaststroke Technique', url: 'https://www.youtube.com/watch?v=JvPLSiyWblg', source: 'USA Swimming' },
        { title: 'How to Swim Breaststroke', url: 'https://www.youtube.com/watch?v=g6gDFDQvlg4', source: 'MySwimPro' },
        { title: 'Breaststroke Pullout Tutorial', url: 'https://www.youtube.com/watch?v=qWTXw8xdjas', source: 'Skills NT' }
      ]
    },
    'Butterfly': {
      description: 'The most physically demanding stroke. Both arms move together with a dolphin kick.',
      whatToWatch: [
        'Two kicks per stroke cycle - one small kick at entry, one big kick at pull',
        'Arms recover together over the water',
        'Undulating body motion like a dolphin',
        'Breathing forward, chin stays near the water',
        'Underwater dolphin kicks off walls'
      ],
      keyMoments: ['Underwater dolphin kicks (can be very fast)', 'Two-hand touch required', 'Maintaining stroke through fatigue'],
      videos: [
        { title: 'Butterfly Technique', url: 'https://www.youtube.com/watch?v=PifVc2ZSXZM', source: 'USA Swimming' },
        { title: 'How to Swim Butterfly', url: 'https://www.youtube.com/watch?v=cEm0_AdPzQY', source: 'MySwimPro' },
        { title: 'Dolphin Kick Underwater', url: 'https://www.youtube.com/watch?v=oHYvPBpBv0c', source: 'Skills NT' }
      ]
    },
    'Individual Medley': {
      description: 'All four strokes in one race: Butterfly, Backstroke, Breaststroke, Freestyle - in that order.',
      whatToWatch: [
        'Transitions between strokes at each wall',
        'Butterfly to Back: touch with two hands, flip to back',
        'Back to Breast: must touch on back, then turn',
        'Breast to Free: two hand touch, then go',
        'Pacing - watch for even splits or negative splitting'
      ],
      keyMoments: ['Stroke transitions at each 25/50', 'Maintaining technique when tired', 'Freestyle finish push'],
      videos: [
        { title: 'IM Transitions Guide', url: 'https://www.youtube.com/watch?v=BrKXmtmFINg', source: 'USA Swimming' },
        { title: 'How to Swim the IM', url: 'https://www.youtube.com/watch?v=_vuPqjp8_NI', source: 'MySwimPro' },
        { title: 'IM Race Strategy', url: 'https://www.youtube.com/watch?v=7wj_LxnvJ5k', source: 'SwimSwam' }
      ]
    }
  };

  const [selectedStrokeGuide, setSelectedStrokeGuide] = useState<string | null>(null);

  const renderScanTimesScreen = () => {
    if (!currentAthlete) return (
      <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-slate-200">
        <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-400 font-bold mb-4 uppercase text-xs">No Athlete Selected</p>
        <button onClick={() => handleTabChange('roster')} className="bg-blue-600 text-white px-6 py-3 rounded-xl font-black uppercase text-xs">Choose Swimmer</button>
      </div>
    );

    return (
      <div className="max-w-2xl mx-auto space-y-6 pb-20">
        <button onClick={() => setCurrentScreen('dashboard')} className="flex items-center text-slate-500 font-bold text-xs uppercase tracking-widest hover:text-slate-800 transition-colors">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back to Dashboard
        </button>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
          <div className="flex items-center space-x-3 mb-6">
            <div className="bg-green-600 p-2 rounded-lg">
              <Camera className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-black text-slate-900 italic uppercase">Scan Heat Sheet</h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase">Upload a photo to extract times for {currentAthlete.name}</p>
            </div>
          </div>

          {/* Upload Area */}
          <input 
            type="file" 
            ref={heatSheetInputRef} 
            onChange={handleHeatSheetUpload} 
            accept="image/*" 
            multiple
            className="hidden" 
          />
          
          {/* Upload Button - Always visible */}
          <button 
            onClick={() => heatSheetInputRef.current?.click()}
            className="w-full border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-green-400 hover:bg-green-50 transition-all group"
          >
            <Upload className="w-10 h-10 text-slate-300 group-hover:text-green-500 mx-auto mb-3" />
            <p className="text-slate-500 font-bold uppercase text-xs group-hover:text-green-600">
              {heatSheetPreviews.length > 0 ? 'Add More Images' : 'Click to Upload Heat Sheet Images'}
            </p>
            <p className="text-slate-400 text-[10px] mt-2">Supports PNG, JPG, HEIC • Select multiple files</p>
          </button>

          {/* Image Previews Grid */}
          {heatSheetPreviews.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-black text-slate-700 uppercase">{heatSheetPreviews.length} Image{heatSheetPreviews.length > 1 ? 's' : ''} Selected</p>
                <button 
                  onClick={() => { setHeatSheetPreviews([]); setExtractedTimes([]); }}
                  className="text-xs font-bold text-red-500 uppercase hover:text-red-600"
                >
                  Clear All
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {heatSheetPreviews.map((preview, index) => (
                  <div key={index} className="relative group">
                    <img src={preview.data} alt={`Heat sheet ${index + 1}`} className="w-full h-32 object-cover rounded-lg border border-slate-200" />
                    <button 
                      onClick={() => removeHeatSheetImage(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    <p className="absolute bottom-1 left-1 right-1 text-[8px] text-white bg-black/50 px-1 py-0.5 rounded truncate">{preview.name}</p>
                  </div>
                ))}
              </div>
              
              {extractedTimes.length === 0 && (
                <button 
                  onClick={handleExtractTimes}
                  disabled={isExtractingTimes}
                  className="w-full bg-green-600 text-white py-4 rounded-xl font-black uppercase tracking-widest shadow-xl hover:bg-green-700 disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  {isExtractingTimes ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      <span>{extractionProgress ? `Analyzing ${extractionProgress.current}/${extractionProgress.total}...` : 'Analyzing...'}</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      <span>Extract Times from {heatSheetPreviews.length} Image{heatSheetPreviews.length > 1 ? 's' : ''}</span>
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Extracted Times */}
        {extractedTimes.length > 0 && (
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 space-y-6">
            {/* Meet Details Form */}
            <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
              <h4 className="text-sm font-black text-blue-800 uppercase mb-4 flex items-center">
                <Info className="w-4 h-4 mr-2" /> Meet Details (Required)
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Meet Name *</label>
                  <input 
                    type="text" 
                    value={scanMeetDetails.meetName}
                    onChange={e => setScanMeetDetails(prev => ({ ...prev, meetName: e.target.value }))}
                    placeholder="e.g. Winter Championships"
                    className="w-full bg-white border border-blue-200 rounded-lg px-3 py-2 text-sm font-bold text-slate-900 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Meet Date *</label>
                  <input 
                    type="date" 
                    value={scanMeetDetails.date}
                    onChange={e => setScanMeetDetails(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full bg-white border border-blue-200 rounded-lg px-3 py-2 text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Course</label>
                  <select 
                    value={scanMeetDetails.course}
                    onChange={e => setScanMeetDetails(prev => ({ ...prev, course: e.target.value }))}
                    className="w-full bg-white border border-blue-200 rounded-lg px-3 py-2 text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="SCY">SCY (Short Course Yards)</option>
                    <option value="SCM">SCM (Short Course Meters)</option>
                    <option value="LCM">LCM (Long Course Meters)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Extracted Times List */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-lg font-black text-slate-900 uppercase">Extracted Times ({extractedTimes.length})</h4>
                  <p className="text-[10px] text-slate-500">{extractedTimes.filter(t => t.selected).length} selected • Times for {currentAthlete?.name} auto-selected</p>
                </div>
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={() => setExtractedTimes(prev => prev.map(t => ({ 
                      ...t, 
                      selected: t.swimmerName?.toLowerCase().includes(currentAthlete?.name.toLowerCase().split(' ')[0] || '') 
                    })))}
                    className="text-[10px] font-bold text-purple-600 uppercase px-2 py-1 bg-purple-50 rounded"
                  >
                    Select {currentAthlete?.name.split(' ')[0]}'s
                  </button>
                  <button 
                    onClick={() => setExtractedTimes(prev => prev.map(t => ({ ...t, selected: !prev.every(x => x.selected) })))}
                    className="text-[10px] font-bold text-blue-600 uppercase px-2 py-1 bg-blue-50 rounded"
                  >
                    {extractedTimes.every(t => t.selected) ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
              </div>

              <div className="space-y-2 max-h-72 overflow-y-auto">
                {extractedTimes.map((time, index) => (
                  <div 
                    key={index}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      time.selected 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-slate-200 bg-slate-50 opacity-60'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div 
                          onClick={() => toggleExtractedTime(index)}
                          className={`w-6 h-6 rounded-full flex items-center justify-center cursor-pointer ${time.selected ? 'bg-green-500' : 'bg-slate-300'}`}
                        >
                          {time.selected && <CheckCircle2 className="w-4 h-4 text-white" />}
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <p className="font-black text-slate-800">{time.matchedEventName || time.eventName || `${time.distance} ${time.stroke}`}</p>
                            {time.matchConfidence && (
                              <span className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded ${
                                time.matchConfidence === 'high' ? 'bg-green-100 text-green-700' :
                                time.matchConfidence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {time.matchConfidence === 'high' ? 'Matched' : time.matchConfidence === 'medium' ? 'Partial' : 'New'}
                              </span>
                            )}
                          </div>
                          <p className="text-[10px] text-slate-500 uppercase font-bold">{time.swimmerName}</p>
                          <p className="text-[9px] text-slate-400">{time.place ? `${time.place}${['st','nd','rd'][time.place-1] || 'th'} Place` : ''} {time.heat ? `Heat ${time.heat}` : ''} {time.lane ? `Lane ${time.lane}` : ''}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-black text-green-600">{time.timeStr}</p>
                        <p className="text-[9px] text-slate-400">{time.distance}y {time.stroke}</p>
                      </div>
                    </div>
                    {/* Manual event mapping dropdown for non-high-confidence matches */}
                    {time.matchConfidence !== 'high' && (
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <label className="block text-[9px] font-bold text-amber-600 uppercase mb-1">Map to event:</label>
                        <select
                          data-testid={`event-map-select-${index}`}
                          value={time.matchedEventId || ''}
                          onClick={e => e.stopPropagation()}
                          onChange={e => {
                            const selectedEvent = events.find(ev => ev.id === e.target.value);
                            setExtractedTimes(prev => prev.map((t, i) => i === index ? {
                              ...t,
                              matchedEventId: e.target.value || undefined,
                              matchedEventName: selectedEvent?.name || t.matchedEventName,
                              matchConfidence: e.target.value ? 'high' : t.matchConfidence,
                              distance: selectedEvent?.distance || t.distance,
                              stroke: selectedEvent?.stroke || t.stroke
                            } : t));
                          }}
                          className="w-full bg-white border border-amber-200 rounded-lg px-2 py-1.5 text-xs font-bold outline-none focus:ring-2 focus:ring-amber-400"
                        >
                          <option value="">-- Create as new event --</option>
                          {events
                            .filter(e => e.ageGroup === currentAthlete?.ageGroup)
                            .map(e => (
                              <option key={e.id} value={e.id}>{e.name} ({e.course})</option>
                            ))
                          }
                        </select>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <button 
              onClick={handleImportExtractedTimes}
              disabled={!extractedTimes.some(t => t.selected) || !scanMeetDetails.meetName.trim() || isImportingTimes}
              className="w-full bg-blue-600 text-white py-4 rounded-xl font-black uppercase tracking-widest shadow-xl hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              {isImportingTimes ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  <span>Importing...</span>
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  <span>Import {extractedTimes.filter(t => t.selected).length} Time(s)</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderFocusScreen = () => {
    if (!currentAthlete) return (
      <div className="text-center py-20 glass-card rounded-2xl">
        <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
        <p className="text-slate-400 font-bold mb-4 uppercase text-xs">No Athlete Selected</p>
        <button onClick={() => handleTabChange('roster')} className="bg-sky-500 hover:bg-sky-400 text-white px-6 py-3 rounded-xl font-bold uppercase text-xs glow-blue transition-all btn-press">Choose Swimmer</button>
      </div>
    );

    return (
      <div className="space-y-6 pb-20">
        {/* AI Technique Coach Section */}
        <div className="glass-card rounded-2xl p-6 relative overflow-hidden">
          <Sparkles className="absolute -right-2 -top-2 w-24 h-24 text-sky-500/10" />
          <div className="flex items-center justify-between mb-4 relative z-10">
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-br from-sky-500 to-blue-600 p-3 rounded-xl shadow-lg shadow-sky-500/30"><Lightbulb className="w-5 h-5 text-white" /></div>
              <div>
                <h3 className="font-display text-xl font-bold text-white uppercase">AI Technique Coach</h3>
                <p className="text-[10px] text-slate-500 font-medium">Personalized tips based on {currentAthlete.name}'s times</p>
              </div>
            </div>
            <button onClick={handleGenerateStrokeInsights} disabled={isGeneratingInsights} className="bg-white/5 hover:bg-white/10 border border-white/10 px-4 py-2 rounded-xl transition-all flex items-center space-x-2 btn-press">
              {isGeneratingInsights ? <RefreshCw className="w-4 h-4 text-sky-400 animate-spin" /> : <RefreshCw className="w-4 h-4 text-sky-400" />}
              <span className="text-xs font-bold uppercase text-white">{isGeneratingInsights ? 'Analyzing...' : 'Generate Tips'}</span>
            </button>
          </div>
          
          {Object.keys(strokeInsights).length === 0 && !isGeneratingInsights && (
            <div className="bg-slate-900/60 rounded-xl p-4 border border-white/5">
              <p className="text-sm text-slate-500 text-center">Click "Generate Tips" to get AI-powered technique advice based on recorded times</p>
            </div>
          )}

          {Object.keys(strokeInsights).length > 0 && (
            <div className="space-y-3 mt-4">
              {Object.entries(strokeInsights).map(([stroke, tip]) => (
                <div key={stroke} className="bg-slate-900/60 rounded-xl p-4 border border-white/5">
                  <div className="flex items-center space-x-2 mb-2">
                    <Award className="w-4 h-4 text-sky-400" />
                    <p className="text-xs font-bold uppercase text-sky-400">{stroke}</p>
                  </div>
                  <p className="text-sm text-slate-300 leading-relaxed">{tip}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Stroke Guide for Parents & Fans */}
        <div className="glass-card rounded-2xl p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="bg-gradient-to-br from-purple-500 to-pink-600 p-3 rounded-xl shadow-lg shadow-purple-500/30"><Heart className="w-5 h-5 text-white" /></div>
            <div>
              <h3 className="font-display text-xl font-bold text-white uppercase">Stroke Guide</h3>
              <p className="text-[10px] text-slate-500 font-bold uppercase">For Parents & Fans - What to Watch</p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
            {Object.keys(strokeGuide).map(stroke => (
              <button
                key={stroke}
                onClick={() => setSelectedStrokeGuide(selectedStrokeGuide === stroke ? null : stroke)}
                className={`p-3 rounded-xl text-xs font-bold uppercase transition-all btn-press ${
                  selectedStrokeGuide === stroke 
                    ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/30' 
                    : 'bg-slate-900/60 text-slate-400 hover:bg-purple-500/20 hover:text-purple-400 border border-white/5'
                }`}
              >
                {stroke === 'Individual Medley' ? 'IM' : stroke}
              </button>
            ))}
          </div>

          {selectedStrokeGuide && strokeGuide[selectedStrokeGuide] && (
            <div className="bg-slate-900/60 rounded-xl p-5 border border-white/5 space-y-4">
              <div>
                <p className="text-xs font-bold uppercase text-purple-400 mb-2">What is {selectedStrokeGuide}?</p>
                <p className="text-sm text-slate-300 leading-relaxed">{strokeGuide[selectedStrokeGuide].description}</p>
              </div>
              
              <div>
                <p className="text-xs font-bold uppercase text-purple-400 mb-2 flex items-center">
                  <Activity className="w-4 h-4 mr-1" /> What to Watch For
                </p>
                <ul className="space-y-2">
                  {strokeGuide[selectedStrokeGuide].whatToWatch.map((item, i) => (
                    <li key={i} className="flex items-start space-x-2 text-sm text-slate-400">
                      <CheckCircle2 className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <p className="text-xs font-bold uppercase text-purple-400 mb-2 flex items-center">
                  <Zap className="w-4 h-4 mr-1" /> Key Moments to Watch
                </p>
                <div className="flex flex-wrap gap-2">
                  {strokeGuide[selectedStrokeGuide].keyMoments.map((moment, i) => (
                    <span key={i} className="bg-purple-500/20 px-3 py-1.5 rounded-lg text-xs font-bold text-purple-400 border border-purple-500/30">
                      {moment}
                    </span>
                  ))}
                </div>
              </div>

              {/* Video Tutorials Section */}
              <div className="pt-4 border-t border-white/5">
                <p className="text-xs font-bold uppercase text-purple-400 mb-3 flex items-center">
                  <ExternalLink className="w-4 h-4 mr-1" /> Video Tutorials
                </p>
                <div className="space-y-2">
                  {strokeGuide[selectedStrokeGuide].videos.map((video, i) => (
                    <a 
                      key={i} 
                      href={video.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-3 bg-slate-800/60 rounded-xl border border-white/5 hover:border-purple-500/30 hover:bg-purple-500/10 transition-all group"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-bold text-white group-hover:text-purple-400">{video.title}</p>
                          <p className="text-[10px] text-slate-500 font-bold uppercase">{video.source}</p>
                        </div>
                      </div>
                      <ExternalLink className="w-4 h-4 text-slate-500 group-hover:text-purple-400" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}

          {!selectedStrokeGuide && (
            <div className="bg-slate-900/60 rounded-xl p-4 text-center border border-white/5">
              <p className="text-sm text-slate-500">Select a stroke above to learn what to watch for during the race</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderProgress = () => {
    if (!currentAthlete) return (
      <div className="text-center py-20 glass-card rounded-2xl">
        <Activity className="w-12 h-12 text-slate-600 mx-auto mb-4" />
        <p className="text-slate-400 font-bold mb-4 uppercase text-xs">No Athlete Selected</p>
        <button onClick={() => handleTabChange('roster')} className="bg-sky-500 hover:bg-sky-400 text-white px-6 py-3 rounded-xl font-bold uppercase text-xs glow-blue transition-all btn-press">Choose Swimmer</button>
      </div>
    );
    return (
      <div className="space-y-6 pb-20">
        <div className="glass-card rounded-2xl p-5">
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-3 rounded-xl shadow-lg shadow-emerald-500/30"><Activity className="w-5 h-5 text-white" /></div>
            <div>
              <h3 className="font-display text-xl font-bold text-white uppercase">Progress</h3>
              <p className="text-[10px] text-slate-500 font-bold uppercase">{currentAthlete.name} · every event over the season</p>
            </div>
          </div>
        </div>
        <ProgressChart athlete={currentAthlete} times={times} events={events} standards={standards} />
      </div>
    );
  };

  const renderDashboard = () => {
    if (!currentAthlete) return (
      <div className="text-center py-20 glass-card rounded-2xl">
        <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
        <p className="text-slate-400 font-bold mb-4 uppercase text-xs">No Athlete Selected</p>
        <button onClick={() => handleTabChange('roster')} className="bg-sky-500 hover:bg-sky-400 text-white px-6 py-3 rounded-xl font-bold uppercase text-xs glow-blue transition-all btn-press">
          Choose Swimmer
        </button>
      </div>
    );
    
    const selectedEvents = events.filter(e => currentAthlete.selectedEventIds.includes(e.id));
    
    // Apply search/filter
    const filteredEvents = selectedEvents.filter(e => {
      if (eventSearchQuery) {
        const q = eventSearchQuery.toLowerCase();
        const nameMatch = e.name.toLowerCase().includes(q);
        const strokeMatch = e.stroke.toLowerCase().includes(q);
        if (!nameMatch && !strokeMatch) return false;
      }
      if (eventStrokeFilter !== 'all' && e.stroke !== eventStrokeFilter) return false;
      if (eventCourseFilter !== 'all' && e.course !== eventCourseFilter) return false;
      return true;
    });
    
    // Sort events by: 1) State Qualified, 2) Regional Qualified, 3) Closest to Regional, 4) Closest to State
    const sortedEvents = [...filteredEvents].sort((a, b) => {
      const aBestEntry = getBestTime(a.id, currentAthlete.id);
      const bBestEntry = getBestTime(b.id, currentAthlete.id);
      const aTime = aBestEntry?.timeSeconds;
      const bTime = bBestEntry?.timeSeconds;
      
      const getStandardsForEvent = (ev: Event) => getTrackedStandards(currentAthlete, ev, events, standards);
      const aStandards = getStandardsForEvent(a);
      const bStandards = getStandardsForEvent(b);
      
      const aStateCut = aStandards.find(s => s.region === 'State')?.cutTimeSeconds;
      const aRegionalCut = aStandards.find(s => s.region === 'Regional')?.cutTimeSeconds;
      const bStateCut = bStandards.find(s => s.region === 'State')?.cutTimeSeconds;
      const bRegionalCut = bStandards.find(s => s.region === 'Regional')?.cutTimeSeconds;
      
      // Check qualification status
      const aStateQualified = aTime && aStateCut && aTime <= aStateCut;
      const aRegionalQualified = aTime && aRegionalCut && aTime <= aRegionalCut;
      const bStateQualified = bTime && bStateCut && bTime <= bStateCut;
      const bRegionalQualified = bTime && bRegionalCut && bTime <= bRegionalCut;
      
      // Priority 1: State Qualified first
      if (aStateQualified && !bStateQualified) return -1;
      if (!aStateQualified && bStateQualified) return 1;
      
      // Priority 2: Regional Qualified (but not state)
      if (aRegionalQualified && !aStateQualified && (!bRegionalQualified || bStateQualified)) return -1;
      if (bRegionalQualified && !bStateQualified && (!aRegionalQualified || aStateQualified)) return 1;
      
      // Priority 3: Closest to Regional (for those not yet qualified)
      if (!aRegionalQualified && !bRegionalQualified && aTime && bTime && aRegionalCut && bRegionalCut) {
        const aGapToRegional = aTime - aRegionalCut;
        const bGapToRegional = bTime - bRegionalCut;
        if (aGapToRegional !== bGapToRegional) return aGapToRegional - bGapToRegional;
      }
      
      // Priority 4: Closest to State (for those regionally qualified)
      if (aRegionalQualified && bRegionalQualified && !aStateQualified && !bStateQualified && aTime && bTime && aStateCut && bStateCut) {
        const aGapToState = aTime - aStateCut;
        const bGapToState = bTime - bStateCut;
        return aGapToState - bGapToState;
      }
      
      // Events with times before events without
      if (aTime && !bTime) return -1;
      if (!aTime && bTime) return 1;
      
      return 0;
    });
    
    return (
      <div className="space-y-6">
        {/* Hero Card with Season Status */}
        <div className="glass-card rounded-2xl overflow-hidden relative animate-slide-up water-wave">
          <div className="absolute inset-0 bg-gradient-to-r from-sky-600/20 to-blue-900/40" />
          <div className="absolute top-0 right-0 w-40 h-40 opacity-20">
            <img src="https://images.pexels.com/photos/6011889/pexels-photo-6011889.jpeg?auto=compress&cs=tinysrgb&w=200" alt="" className="w-full h-full object-cover" />
          </div>
          <div className="relative p-6">
            <p className="text-[10px] font-bold uppercase tracking-widest text-sky-400 mb-2">Season Status</p>
            <h2 className="font-display text-3xl font-black text-white uppercase leading-tight mb-2">
              Ready for the<br />Heat, {currentAthlete.name.split(' ')[0]}?
            </h2>
            <p className="text-sm text-slate-400">Your qualifying window is open. Time to make waves.</p>
          </div>
        </div>

        {/* Athlete Profile Card */}
        <div className="glass-card p-5 rounded-2xl flex items-center justify-between animate-slide-up" style={{ animationDelay: '0.1s' }}>
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-gradient-to-br from-sky-500 to-blue-600 rounded-2xl flex items-center justify-center text-white font-display font-black text-2xl shadow-lg shadow-sky-500/30 float">
              {currentAthlete.name.charAt(0)}
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Current Profile</p>
              <h2 className="font-display text-2xl font-black text-white uppercase leading-none">{currentAthlete.name}</h2>
              <p className="text-xs font-medium text-sky-400 mt-1">{currentAthlete.ageGroup} • {currentAthlete.gender === 'M' ? 'Male' : 'Female'}</p>
              {(() => {
                const move = currentAthlete.dob ? getNextAgeGroupChange(currentAthlete.dob) : null;
                if (!move) return null;
                return <p className="text-[9px] font-bold text-amber-400 uppercase tracking-widest mt-1">Moves to {move.group} on {move.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>;
              })()}
              <div className="flex flex-wrap items-center gap-1 mt-2" data-testid="track-group-selector">
                <span className="text-[9px] font-bold text-slate-600 uppercase tracking-widest mr-0.5">Cuts:</span>
                {['auto', '10U', '11-12', '13-14', '15-16', '17-18'].map(g => {
                  const active = g === 'auto' ? !currentAthlete.trackAgeGroup : currentAthlete.trackAgeGroup === g;
                  const label = g === 'auto' ? `Auto (${currentAthlete.ageGroup})` : g;
                  return (
                    <button
                      key={g}
                      onClick={() => handleSetTrackGroup(g)}
                      data-testid={`track-group-${g}`}
                      className={`px-2 py-1 rounded-lg text-[9px] font-black uppercase tracking-wide transition-all btn-press ${
                        active
                          ? 'bg-amber-500/25 text-amber-300 border border-amber-500/40'
                          : 'bg-slate-800/60 text-slate-400 border border-white/5 hover:text-white hover:border-sky-500/30'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              {currentAthlete.trackAgeGroup && currentAthlete.trackAgeGroup !== currentAthlete.ageGroup && (() => {
                // The time carries: once the swimmer ages into the pinned group, times that
                // beat those cuts ARE their qualifying times (SWIMS entry is time-vs-standard).
                const firstAge = { '11-12': 11, '13-14': 13, '15-16': 15, '17-18': 17 }[currentAthlete.trackAgeGroup] || null;
                const dob = currentAthlete.dob ? new Date(currentAthlete.dob) : null;
                const first = currentAthlete.name.split(' ')[0];
                let msg = `Watching ${currentAthlete.trackAgeGroup} cuts — targets until ${first} swims ${currentAthlete.trackAgeGroup}`;
                if (firstAge && dob) {
                  const enters = new Date(dob.getFullYear() + firstAge, dob.getMonth(), dob.getDate());
                  if (enters > new Date()) {
                    msg += ` (${enters.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`;
                  }
                }
                msg += '. A time that beats them counts once they age in.';
                return <p className="text-[9px] font-bold text-sky-400 uppercase tracking-widest mt-1">{msg}</p>;
              })()}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button onClick={() => setCurrentScreen('scan-times')} className="bg-emerald-500/10 text-emerald-400 p-3 rounded-xl hover:bg-emerald-500/20 transition-all btn-press icon-bounce" title="Scan Heat Sheet">
              <Camera className="w-5 h-5" />
            </button>
            <button onClick={() => setCurrentScreen('manage-swimmer-events')} className="bg-sky-500/10 text-sky-400 p-3 rounded-xl hover:bg-sky-500/20 transition-all btn-press icon-bounce" title="Manage Events">
              <Edit3 className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Course Tabs */}
        <div className="flex items-center space-x-1 animate-fade-in" style={{ animationDelay: '0.15s' }}>
          {['all', 'SCY', 'LCM', 'SCM'].map(c => {
            const count = c === 'all' ? selectedEvents.length : selectedEvents.filter(e => e.course === c).length;
            if (c !== 'all' && count === 0) return null;
            return (
              <button
                key={c}
                data-testid={`course-tab-${c}`}
                onClick={() => setEventCourseFilter(c)}
                className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
                  eventCourseFilter === c
                    ? c === 'SCY' ? 'bg-sky-500 text-white' 
                    : c === 'LCM' ? 'bg-amber-500 text-white'
                    : c === 'SCM' ? 'bg-purple-500 text-white'
                    : 'bg-white/10 text-white'
                    : 'bg-slate-800/60 text-slate-500 hover:text-slate-300 hover:bg-slate-800'
                }`}
              >
                {c === 'all' ? 'All' : c} <span className="ml-1 opacity-70">{count}</span>
              </button>
            );
          })}
        </div>

        {/* Section Title */}
        <div className="flex items-center justify-between animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <h3 className="font-display text-xl font-bold text-white uppercase tracking-wide">Season Best</h3>
          <div className="flex items-center space-x-3">
            <button 
              data-testid="verify-standards-btn"
              onClick={() => setShowVerifyStandards(true)}
              className="flex items-center space-x-1.5 text-sky-400 text-[10px] font-bold uppercase tracking-widest hover:text-sky-300 transition-colors"
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>Verify Times</span>
            </button>
            <button
              data-testid="verify-all-btn"
              onClick={async () => {
                setIsVerifying(true);
                try {
                  const res = await fetch('/api/ai/verify-all', { method: 'POST', headers: getSessionHeaders(currentUser) });
                  if (res.ok) {
                    const data = await res.json();
                    // Reload standards to get updated scores
                    const stdRes = await fetch('/api/standards', { headers: getSessionHeaders(currentUser) });
                    if (stdRes.ok) setStandards(await stdRes.json());
                    alert(`Verified ${data.verified} events!`);
                  } else {
                    alert('Verification failed. Please try again.');
                  }
                } catch { alert('Verification failed.'); }
                finally { setIsVerifying(false); }
              }}
              disabled={isVerifying}
              className={`flex items-center space-x-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors ${isVerifying ? 'text-slate-600' : 'text-green-400 hover:text-green-300'}`}
            >
              {isVerifying ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /><span>Verifying...</span></> : <><CheckCircle2 className="w-3.5 h-3.5" /><span>Verify All</span></>}
            </button>
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{sortedEvents.length}{sortedEvents.length !== selectedEvents.length ? `/${selectedEvents.length}` : ''} Events</span>
          </div>
        </div>

        {/* Verification Summary */}
        {(() => {
          const verifiedStandards = standards.filter(s => s.verificationScore && sortedEvents.some(e => e.id === s.eventId));
          if (verifiedStandards.length === 0) return null;
          const high = verifiedStandards.filter(s => s.verificationConfidence === 'high').length;
          const med = verifiedStandards.filter(s => s.verificationConfidence === 'medium').length;
          const low = verifiedStandards.filter(s => s.verificationConfidence === 'low').length;
          // Dedupe by eventId (regional + state for same event share same score)
          const uniqueEvents = [...new Set(verifiedStandards.map(s => s.eventId))].length;
          return (
            <div data-testid="verification-summary" className="glass-card p-3 rounded-xl flex items-center justify-between animate-fade-in" style={{ animationDelay: '0.22s' }}>
              <div className="flex items-center space-x-3">
                <CheckCircle2 className="w-4 h-4 text-green-400" />
                <span className="text-xs font-bold text-white">Source Verification ({uniqueEvents} events)</span>
              </div>
              <div className="flex items-center space-x-3 text-[10px] font-bold">
                {high > 0 && <span className="text-green-400 bg-green-500/10 px-2 py-1 rounded">{Math.ceil(high/2)} confirmed</span>}
                {med > 0 && <span className="text-amber-400 bg-amber-500/10 px-2 py-1 rounded">{Math.ceil(med/2)} partial</span>}
                {low > 0 && <span className="text-red-400 bg-red-500/10 px-2 py-1 rounded">{Math.ceil(low/2)} review</span>}
              </div>
            </div>
          );
        })()}

        {/* Search & Filter Bar */}
        {selectedEvents.length > 3 && (
          <div data-testid="event-filter-bar" className="glass-card p-3 rounded-xl flex flex-wrap items-center gap-2 animate-fade-in" style={{ animationDelay: '0.25s' }}>
            <div className="relative flex-1 min-w-[140px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
              <input
                data-testid="event-search-input"
                type="text"
                value={eventSearchQuery}
                onChange={e => setEventSearchQuery(e.target.value)}
                placeholder="Search events..."
                className="w-full bg-slate-900/60 border border-white/10 rounded-lg pl-8 pr-3 py-2 text-xs font-bold text-white outline-none focus:border-sky-500/50 placeholder:text-slate-600 transition-all"
              />
            </div>
            <select
              data-testid="stroke-filter-select"
              value={eventStrokeFilter}
              onChange={e => setEventStrokeFilter(e.target.value)}
              className="bg-slate-900/60 border border-white/10 rounded-lg px-2 py-2 text-[10px] font-bold text-slate-300 uppercase outline-none focus:border-sky-500/50"
            >
              <option value="all">All Strokes</option>
              <option value="Freestyle">Free</option>
              <option value="Backstroke">Back</option>
              <option value="Breaststroke">Breast</option>
              <option value="Butterfly">Fly</option>
              <option value="Individual Medley">IM</option>
            </select>
            {(eventSearchQuery || eventStrokeFilter !== 'all') && (
              <button
                onClick={() => { setEventSearchQuery(''); setEventStrokeFilter('all'); }}
                className="text-[10px] font-bold text-red-400 uppercase px-2 py-2 hover:text-red-300"
              >
                Clear
              </button>
            )}
          </div>
        )}

        {/* Event Cards */}
        {sortedEvents.length === 0 ? (
          <div className="text-center py-16 glass-card rounded-2xl">
            <Info className="w-10 h-10 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 font-bold text-sm uppercase mb-4">No events selected</p>
            <button onClick={() => setCurrentScreen('manage-swimmer-events')} className="bg-sky-500 hover:bg-sky-400 text-white px-6 py-3 rounded-xl font-bold uppercase text-xs glow-blue transition-all btn-press">
              Manage My Events
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {(eventCourseFilter === 'all' ? ['SCY', 'LCM', 'SCM'] : [eventCourseFilter]).map(courseGroup => {
              const courseEvents = sortedEvents.filter(e => e.course === courseGroup);
              if (courseEvents.length === 0) return null;
              return (
                <div key={courseGroup}>
                  {eventCourseFilter === 'all' && (
                    <div className="flex items-center space-x-2 mb-3">
                      <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded ${
                        courseGroup === 'SCY' ? 'bg-sky-500/20 text-sky-400' : courseGroup === 'LCM' ? 'bg-amber-500/20 text-amber-400' : 'bg-purple-500/20 text-purple-400'
                      }`}>{courseGroup}</span>
                      <span className="text-[10px] text-slate-600 font-bold uppercase">
                        {courseGroup === 'SCY' ? 'Short Course Yards' : courseGroup === 'LCM' ? 'Long Course Meters' : 'Short Course Meters'}
                      </span>
                      <span className="text-[9px] text-slate-700 font-bold">{courseEvents.length} events</span>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {courseEvents.map((event, index) => (
                      <div key={event.id} style={{ animationDelay: `${index * 0.05}s` }} className="animate-slide-up opacity-0" 
                           onAnimationEnd={(e) => (e.currentTarget.style.opacity = '1')}>
                <DashboardCard 
                  event={event} 
                  bestTime={getBestTime(event.id, currentAthlete.id)} 
                  standards={getTrackedStandards(currentAthlete, event, events, standards)} 
                  athleteGender={currentAthlete.gender} 
                  standardsGroup={currentAthlete.trackAgeGroup && currentAthlete.trackAgeGroup !== event.ageGroup ? currentAthlete.trackAgeGroup : undefined}
                  onClick={() => { setSelectedEventId(event.id); setCurrentScreen('event-detail'); }}
                  onEditStandard={handleEditStandard}
                  onCreateStandard={(region, cutTime) => handleCreateStandard(event.id, event.ageGroup, currentAthlete.gender, event.course, region, cutTime)}
                  verification={verificationResults[event.id] || null}
                />
              </div>
            ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderAdmin = () => (
    <div className="space-y-6 pb-20">
      <div className="bg-slate-900 rounded-2xl p-6 text-white shadow-xl border border-slate-800 relative overflow-hidden">
        <Sparkles className="absolute -right-2 -top-2 w-24 h-24 text-blue-500/10" />
        <div className="flex space-x-6 mb-8 relative z-10 border-b border-slate-800 pb-4 overflow-x-auto no-scrollbar">
          {['explorer', 'search', 'events'].map(mode => <button key={mode} onClick={() => setAdminMode(mode as any)} className={`pb-2 text-xs font-black uppercase tracking-widest whitespace-nowrap transition-all ${adminMode === mode ? 'text-blue-500 border-b-2 border-blue-500' : 'text-slate-500'}`}>{mode}</button>)}
        </div>

        <div className="relative z-10">
          {adminMode === 'explorer' && (
            <div className="space-y-6">
              <div className="flex flex-wrap gap-3 bg-slate-800 p-4 rounded-xl border border-slate-700">
                <div className="flex items-center space-x-2"><Filter className="w-4 h-4 text-blue-500" /><select value={explorerFilter.ageGroup} onChange={e => setExplorerFilter({...explorerFilter, ageGroup: e.target.value})} className="bg-transparent text-xs font-black uppercase outline-none focus:text-white"><option value="10U">10U</option><option value="11-12">11-12</option><option value="13-14">13-14</option><option value="15-16">15-16</option><option value="17-18">17-18</option></select></div>
                <div className="flex items-center space-x-2 border-l border-slate-700 pl-3"><UserIcon className="w-4 h-4 text-pink-500" /><select value={explorerFilter.gender} onChange={e => setExplorerFilter({...explorerFilter, gender: e.target.value as 'M' | 'F'})} className="bg-transparent text-xs font-black uppercase outline-none focus:text-white"><option value="M">Boys</option><option value="F">Girls</option></select></div>
                <div className="flex items-center space-x-2 border-l border-slate-700 pl-3"><Ruler className="w-4 h-4 text-blue-400" /><select value={explorerFilter.course} onChange={e => setExplorerFilter({...explorerFilter, course: e.target.value as Course})} className="bg-transparent text-xs font-black uppercase outline-none focus:text-white"><option value={Course.SCY}>SCY (Short Course Yards)</option><option value={Course.SCM}>SCM (Short Course Meters)</option><option value={Course.LCM}>LCM (Long Course Meters)</option></select></div>
                <button data-testid="verify-standards-admin-btn" onClick={() => setShowVerifyStandards(true)} className="ml-auto flex items-center space-x-1.5 bg-sky-500/10 text-sky-400 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide hover:bg-sky-500/20 transition-all"><ShieldCheck className="w-3.5 h-3.5" /><span>Verify Official Times</span></button>
              </div>
              <div className="space-y-3">
                  {events.filter(e => e.course === explorerFilter.course && e.ageGroup === explorerFilter.ageGroup).map(ev => {
                    const evCuts = standards.filter(s => s.eventId === ev.id && s.ageGroup === explorerFilter.ageGroup && s.gender === explorerFilter.gender && s.course === explorerFilter.course);
                    return (
                      <div key={ev.id} className="bg-slate-800/40 p-4 rounded-xl border border-slate-700/50 flex justify-between items-center">
                        <div><p className="font-bold text-sm">{ev.name}</p><p className="text-[9px] text-slate-500 font-black uppercase">{ev.stroke}</p></div>
                        <div className="flex space-x-4">
                            {evCuts.length === 0 ? <p className="text-[9px] text-slate-600 font-black uppercase italic">No cuts</p> : evCuts.map(cut => <div key={cut.id} className="text-right"><p className="text-[8px] font-black uppercase text-slate-500">{cut.region}</p><p className="font-mono text-blue-400 font-black text-xs">{formatTime(cut.cutTimeSeconds)}</p></div>)}
                        </div>
                      </div>
                    )
                  })}
              </div>
            </div>
          )}

          {adminMode === 'search' && (
            <div className="space-y-6">
              <form onSubmit={handleResearchStandards} className="bg-slate-800 p-4 rounded-xl border border-slate-700 space-y-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Search className="w-4 h-4 text-blue-500" />
                  <p className="text-[10px] font-black uppercase text-slate-400">AI Research Standards</p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <select name="ageGroup" required className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs font-bold text-white">
                    <option value="10U">10U</option>
                    <option value="11-12">11-12</option>
                    <option value="13-14">13-14</option>
                    <option value="15-16">15-16</option>
                    <option value="17-18">17-18</option>
                  </select>
                  <select name="gender" required className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs font-bold text-white">
                    <option value="M">Boys</option>
                    <option value="F">Girls</option>
                  </select>
                  <select name="season" required className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs font-bold text-white">
                    <option value="2026">2025-2026</option>
                    <option value="2025">2024-2025</option>
                    <option value="2024">2023-2024</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <select name="stateLocation" required className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs font-bold text-white">
                    <option value="">Select LSC...</option>
                    <option value="Adirondack Swimming (AD)">Adirondack (AD)</option>
                    <option value="Alaska Swimming (AK)">Alaska (AK)</option>
                    <option value="Allegheny Mountain Swimming (AM)">Allegheny Mountain (AM)</option>
                    <option value="Arizona Swimming (AZ)">Arizona (AZ)</option>
                    <option value="Arkansas Swimming (AR)">Arkansas (AR)</option>
                    <option value="Border Swimming (BD)">Border (BD)</option>
                    <option value="Central California Swimming (CC)">Central California (CC)</option>
                    <option value="Colorado Swimming (CO)">Colorado (CO)</option>
                    <option value="Connecticut Swimming (CT)">Connecticut (CT)</option>
                    <option value="Florida Gold Coast Swimming (FG)">Florida Gold Coast (FG)</option>
                    <option value="Florida Swimming (FL)">Florida (FL)</option>
                    <option value="Georgia Swimming (GA)">Georgia (GA)</option>
                    <option value="Gulf Swimming (GU)">Gulf (GU)</option>
                    <option value="Hawaiian Swimming (HI)">Hawaiian (HI)</option>
                    <option value="Illinois Swimming (IL)">Illinois (IL)</option>
                    <option value="Indiana Swimming (IN)">Indiana (IN)</option>
                    <option value="Inland Empire Swimming (IE)">Inland Empire (IE)</option>
                    <option value="Iowa Swimming (IA)">Iowa (IA)</option>
                    <option value="Kentucky Swimming (KY)">Kentucky (KY)</option>
                    <option value="Lake Erie Swimming (LE)">Lake Erie (LE)</option>
                    <option value="Louisiana Swimming (LA)">Louisiana (LA)</option>
                    <option value="Maine Swimming (ME)">Maine (ME)</option>
                    <option value="Maryland Swimming (MD)">Maryland (MD)</option>
                    <option value="Metropolitan Swimming (MR)">Metropolitan (MR)</option>
                    <option value="Michigan Swimming (MI)">Michigan (MI)</option>
                    <option value="Middle Atlantic Swimming (MA)">Middle Atlantic (MA)</option>
                    <option value="Midwestern Swimming (MW)">Midwestern (MW)</option>
                    <option value="Minnesota Swimming (MN)">Minnesota (MN)</option>
                    <option value="Mississippi Swimming (MS)">Mississippi (MS)</option>
                    <option value="Missouri Valley Swimming (MV)">Missouri Valley (MV)</option>
                    <option value="Montana Swimming (MT)">Montana (MT)</option>
                    <option value="New England Swimming (NE)">New England (NE)</option>
                    <option value="New Jersey Swimming (NJ)">New Jersey (NJ)</option>
                    <option value="New Mexico Swimming (NM)">New Mexico (NM)</option>
                    <option value="Niagara Swimming (NI)">Niagara (NI)</option>
                    <option value="North Carolina Swimming (NC)">North Carolina (NC)</option>
                    <option value="North Dakota Swimming (ND)">North Dakota (ND)</option>
                    <option value="North Texas Swimming (NT)">North Texas (NT)</option>
                    <option value="Ohio Swimming (OH)">Ohio (OH)</option>
                    <option value="Oklahoma Swimming (OK)">Oklahoma (OK)</option>
                    <option value="Oregon Swimming (OR)">Oregon (OR)</option>
                    <option value="Ozark Swimming (OZ)">Ozark (OZ)</option>
                    <option value="Pacific Northwest Swimming (PN)">Pacific Northwest (PN)</option>
                    <option value="Pacific Swimming (PC)">Pacific (PC)</option>
                    <option value="Potomac Valley Swimming (PV)">Potomac Valley (PV)</option>
                    <option value="San Diego Imperial Swimming (SI)">San Diego Imperial (SI)</option>
                    <option value="Sierra Nevada Swimming (SN)">Sierra Nevada (SN)</option>
                    <option value="Snake River Swimming (SR)">Snake River (SR)</option>
                    <option value="South Carolina Swimming (SC)">South Carolina (SC)</option>
                    <option value="South Dakota Swimming (SD)">South Dakota (SD)</option>
                    <option value="South Texas Swimming (ST)">South Texas (ST)</option>
                    <option value="Southeastern Swimming (SE)">Southeastern (SE)</option>
                    <option value="Southern California Swimming (CA)">Southern California (CA)</option>
                    <option value="Utah Swimming (UT)">Utah (UT)</option>
                    <option value="Virginia Swimming (VA)">Virginia (VA)</option>
                    <option value="West Texas Swimming (WT)">West Texas (WT)</option>
                    <option value="West Virginia Swimming (WV)">West Virginia (WV)</option>
                    <option value="Wisconsin Swimming (WI)">Wisconsin (WI)</option>
                    <option value="Wyoming Swimming (WY)">Wyoming (WY)</option>
                  </select>
                  <select name="course" required className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs font-bold text-white">
                    <option value={Course.SCY}>SCY (Short Course Yards)</option>
                    <option value={Course.SCM}>SCM (Short Course Meters)</option>
                    <option value={Course.LCM}>LCM (Long Course Meters)</option>
                  </select>
                </div>
                <button type="submit" disabled={isResearching} className="w-full bg-blue-600 py-3 rounded-lg font-black uppercase text-[10px] flex items-center justify-center hover:bg-blue-500 transition-all disabled:opacity-50">
                  {isResearching ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Searching...</> : <><Search className="w-4 h-4 mr-2" /> Research Standards</>}
                </button>
                
                {/* Upload/Download Official PDF */}
                <div className="flex items-center space-x-2 mt-3">
                  <button
                    type="button"
                    data-testid="download-lsc-pdf-btn"
                    onClick={async () => {
                      const form = document.querySelector('form') as HTMLFormElement;
                      if (!form) return;
                      const fd = new FormData(form);
                      setIsResearching(true);
                      try {
                        const res = await fetch('/api/lsc-documents/download', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'X-User-Session': JSON.stringify(currentUser) },
                          body: JSON.stringify({ stateLocation: fd.get('stateLocation'), course: fd.get('course'), season: fd.get('season') })
                        });
                        if (res.ok) {
                          const data = await res.json();
                          alert(`Downloaded "${data.fileName}" — extracted ${data.extractedCount} events. Use "Research Standards" to apply them.`);
                        } else {
                          const err = await res.json().catch(() => ({}));
                          alert(err.detail || 'Failed to download PDF. Try uploading manually.');
                        }
                      } catch { alert('Download failed.'); }
                      finally { setIsResearching(false); }
                    }}
                    className="flex-1 bg-slate-700 hover:bg-slate-600 text-slate-300 font-black uppercase text-xs px-4 py-3 rounded-xl flex items-center justify-center space-x-2 transition-all"
                  >
                    <Download className="w-4 h-4" /><span>Download Official PDF</span>
                  </button>
                  <label className="flex-1 bg-slate-700 hover:bg-slate-600 text-slate-300 font-black uppercase text-xs px-4 py-3 rounded-xl flex items-center justify-center space-x-2 transition-all cursor-pointer">
                    <Upload className="w-4 h-4" /><span>Upload PDF</span>
                    <input type="file" accept=".pdf" className="hidden" onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const form = document.querySelector('form') as HTMLFormElement;
                      if (!form) return;
                      const fd = new FormData(form);
                      const reader = new FileReader();
                      reader.onload = async () => {
                        const b64 = (reader.result as string).split(',')[1];
                        setIsResearching(true);
                        try {
                          const res = await fetch('/api/lsc-documents/download', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'X-User-Session': JSON.stringify(currentUser) },
                            body: JSON.stringify({ stateLocation: fd.get('stateLocation'), course: fd.get('course'), season: fd.get('season'), pdfBase64: b64, fileName: file.name })
                          });
                          if (res.ok) {
                            const data = await res.json();
                            alert(`Uploaded "${data.fileName}" — extracted ${data.extractedCount} events.`);
                          } else { alert('Upload failed.'); }
                        } catch { alert('Upload failed.'); }
                        finally { setIsResearching(false); }
                      };
                      reader.readAsDataURL(file);
                    }} />
                  </label>
                </div>
              </form>

              {(groundingLinks.length > 0 || sourceLinks) && (
                <div className="bg-slate-800/60 p-4 rounded-xl border border-slate-700/50 space-y-3">
                  <div className="flex items-start space-x-2">
                    <ExternalLink className="w-4 h-4 text-sky-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] font-black uppercase text-sky-400 mb-1">Verify Sources — Click to confirm times</p>
                      <p className="text-[9px] text-slate-400">Compare AI-researched times against official documents below.</p>
                    </div>
                  </div>
                  
                  {/* Official Links */}
                  <div className="flex flex-wrap gap-2">
                    <a href="https://www.usaswimming.org/times/time-standards" target="_blank" rel="noopener noreferrer" 
                       className="flex items-center space-x-1.5 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 hover:text-blue-300 px-3 py-2 rounded-lg transition-colors border border-blue-500/20">
                      <ExternalLink className="w-3.5 h-3.5" />
                      <span className="text-[10px] font-bold">USA Swimming Motivational Times</span>
                    </a>
                    {sourceLinks?.lscWebsite && (
                      <a href={sourceLinks.lscWebsite} target="_blank" rel="noopener noreferrer" 
                         className="flex items-center space-x-1.5 bg-green-600/20 hover:bg-green-600/30 text-green-400 hover:text-green-300 px-3 py-2 rounded-lg transition-colors border border-green-500/20">
                        <ExternalLink className="w-3.5 h-3.5" />
                        <span className="text-[10px] font-bold">{sourceLinks.lscName} Swimming (LSC)</span>
                      </a>
                    )}
                  </div>
                  
                  {/* PDF & Web Sources from Research */}
                  {groundingLinks.length > 0 && (
                    <div>
                      <p className="text-[8px] font-bold uppercase text-slate-600 mb-1.5">Sources Used by AI</p>
                      <div className="flex flex-wrap gap-1.5">
                        {groundingLinks.slice(0, 5).map((link, i) => (
                          <a key={i} href={link.uri} target="_blank" rel="noopener noreferrer" 
                             className="flex items-center space-x-1 bg-slate-700/50 hover:bg-slate-700 text-slate-400 hover:text-slate-200 px-2 py-1 rounded transition-colors text-[9px] font-medium">
                            <ExternalLink className="w-2.5 h-2.5" />
                            <span className="truncate max-w-[200px]">{link.title || link.uri.split('/').pop()}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {researchResults.length > 0 && (
                <div className="space-y-3">
                  {/* Validation Summary */}
                  {researchResults.some(r => !r.validated) && (
                    <div className="bg-red-900/30 p-3 rounded-xl border border-red-600/50 flex items-start space-x-2">
                      <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-[10px] font-black uppercase text-red-400">Validation Issues Detected</p>
                        <p className="text-[9px] text-red-300/70">Some times were auto-corrected (State must be faster than Regional). Please verify before applying.</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Edit Warning */}
                  <div className="bg-amber-900/20 p-3 rounded-xl border border-amber-600/30 flex items-start space-x-2">
                    <Edit3 className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] font-black uppercase text-amber-400">Click Times to Edit</p>
                      <p className="text-[9px] text-amber-300/70">AI times may be incorrect. Click any time to manually correct it before applying.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-black uppercase text-slate-400">Found {researchResults.length} Events</p>
                      <p className="text-[8px] text-slate-500">{researchResults.filter(r => r.verificationConfidence === 'high').length} confirmed • {researchResults.filter(r => r.verificationConfidence === 'medium').length} partial • {researchResults.filter(r => r.verificationConfidence === 'low' || !r.verificationConfidence).length} unverified</p>
                    </div>
                    <button onClick={() => setShowApplyConfirm(true)} disabled={isApplyingResults} className="bg-green-600 hover:bg-green-500 text-white text-[9px] font-black uppercase px-3 py-1.5 rounded-lg flex items-center space-x-1 disabled:opacity-50">
                      {isApplyingResults ? <><RefreshCw className="w-3 h-3 animate-spin" /><span>Applying...</span></> : <><Save className="w-3 h-3" /><span>Apply All</span></>}
                    </button>
                  </div>
                  
                  <div className="max-h-80 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                    {researchResults.map((res, i) => (
                      <div key={i} className={`p-3 rounded-xl border flex flex-col space-y-2 ${
                        res.validated 
                          ? 'bg-slate-800/40 border-slate-700/50' 
                          : 'bg-amber-900/20 border-amber-600/50'
                      }`}>
                        <div className="flex justify-between items-start">
                          <div className="flex items-center space-x-2">
                            {res.validated ? (
                              <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                            )}
                            <div>
                              <p className="font-bold text-sm">{res.name}</p>
                              <p className="text-[9px] text-slate-500 font-black uppercase">{res.ageGroup} • {res.gender === 'M' ? 'Boys' : 'Girls'} • {res.distance}y</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="text-right">
                              <p className="text-[8px] font-black uppercase text-blue-400 mb-1">Regional (slower)</p>
                              <input 
                                type="text" 
                                value={res.regionalTimeStr || ''} 
                                onChange={(e) => {
                                  const newResults = [...researchResults];
                                  newResults[i] = { ...newResults[i], regionalTimeStr: e.target.value };
                                  setResearchResults(newResults);
                                }}
                                className="w-20 bg-slate-900 border border-blue-500/50 rounded px-2 py-1 text-sm font-mono text-blue-300 font-bold text-center focus:border-blue-400 focus:outline-none"
                                placeholder="MM:SS.ss"
                              />
                            </div>
                            <div className="text-center text-slate-600">→</div>
                            <div className="text-right">
                              <p className="text-[8px] font-black uppercase text-green-400 mb-1">State (faster)</p>
                              <input 
                                type="text" 
                                value={res.stateTimeStr || ''} 
                                onChange={(e) => {
                                  const newResults = [...researchResults];
                                  newResults[i] = { ...newResults[i], stateTimeStr: e.target.value };
                                  setResearchResults(newResults);
                                }}
                                className="w-20 bg-slate-900 border border-green-500/50 rounded px-2 py-1 text-sm font-mono text-green-300 font-bold text-center focus:border-green-400 focus:outline-none"
                                placeholder="MM:SS.ss"
                              />
                            </div>
                            <button onClick={() => syncSingleResult(res)} className="bg-blue-600/20 hover:bg-blue-600/40 p-2 rounded-lg transition-all ml-1" title="Apply this event">
                              <Plus className="w-4 h-4 text-blue-400" />
                            </button>
                          </div>
                        </div>
                        {res.warnings && res.warnings.length > 0 && (
                          <div className="bg-amber-900/30 px-2 py-1 rounded text-[9px] text-amber-300">
                            ⚠️ {res.warnings.join(' • ')}
                          </div>
                        )}
                        {res.source && (
                          <p className="text-[8px] text-slate-500">Source: {res.source}</p>
                        )}
                        {res.verificationScore && (
                          <div className={`flex items-center space-x-1.5 text-[9px] font-bold ${
                            res.verificationConfidence === 'high' ? 'text-green-400' : res.verificationConfidence === 'medium' ? 'text-amber-400' : 'text-red-400'
                          }`}>
                            <CheckCircle2 className="w-3 h-3" />
                            <span>Sources: {res.verificationScore}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Manual Verification Reminder */}
                  <div className="bg-blue-900/20 p-3 rounded-xl border border-blue-600/30 mt-4">
                    <div className="flex items-start space-x-2">
                      <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-[10px] font-black uppercase text-blue-400">Manual Verification Recommended</p>
                        <p className="text-[9px] text-blue-300/70">Always verify times against official sources above before applying. AI research may contain errors.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {!isResearching && researchResults.length === 0 && (
                <div className="text-center py-8">
                  <Search className="w-10 h-10 text-slate-700 mx-auto mb-3" />
                  <p className="text-slate-500 text-xs font-bold">Enter filters above and search to find qualifying standards</p>
                </div>
              )}
            </div>
          )}

          {adminMode === 'events' && (
            <div className="space-y-6">
              {editingEvent ? (
                <form onSubmit={handleUpdateEvent} className="bg-slate-800 p-5 rounded-xl border border-blue-500/50 space-y-4 shadow-2xl animate-in zoom-in-95">
                  <div className="flex justify-between items-center"><p className="text-[10px] font-black uppercase text-blue-400">Editing {editingEvent.name}</p><button type="button" onClick={() => setEditingEvent(null)} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button></div>
                  <div className="grid grid-cols-2 gap-2"><input name="name" type="text" defaultValue={editingEvent.name} required className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-3 text-xs font-bold text-white outline-none focus:ring-1 focus:ring-blue-500" /><input name="distance" type="number" defaultValue={editingEvent.distance} required className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-3 text-xs font-bold text-white outline-none focus:ring-1 focus:ring-blue-500" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <select name="stroke" defaultValue={editingEvent.stroke} className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-3 text-xs font-bold text-white">{Object.values(Stroke).map(s => <option key={s} value={s}>{s}</option>)}</select>
                    <select name="course" defaultValue={editingEvent.course} className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-3 text-xs font-bold text-white">{Object.values(Course).map(c => <option key={c} value={c}>{c}</option>)}</select>
                  </div>
                  <div><select name="ageGroup" defaultValue={editingEvent.ageGroup} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-3 text-xs font-bold text-white"><option value="10U">10U</option><option value="11-12">11-12</option><option value="13-14">13-14</option><option value="15-16">15-16</option><option value="17-18">17-18</option></select></div>
                  
                  <div className="grid grid-cols-2 gap-4 border-t border-slate-700 pt-4 mt-2">
                    <div className="space-y-3">
                      <p className="text-[9px] font-black uppercase text-blue-400">Boys Cuts</p>
                      <input name="regCutM" type="text" placeholder="Reg (mm:ss.xx)" defaultValue={formatTime(standards.find(s => s.eventId === editingEvent.id && s.gender === 'M' && s.region === 'Regional' && s.ageGroup === editingEvent.ageGroup && s.course === editingEvent.course)?.cutTimeSeconds || 0) || ''} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs font-bold text-white" />
                      <input name="stateCutM" type="text" placeholder="State (mm:ss.xx)" defaultValue={formatTime(standards.find(s => s.eventId === editingEvent.id && s.gender === 'M' && s.region === 'State' && s.ageGroup === editingEvent.ageGroup && s.course === editingEvent.course)?.cutTimeSeconds || 0) || ''} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs font-bold text-white" />
                    </div>
                    <div className="space-y-3">
                      <p className="text-[9px] font-black uppercase text-pink-400">Girls Cuts</p>
                      <input name="regCutF" type="text" placeholder="Reg (mm:ss.xx)" defaultValue={formatTime(standards.find(s => s.eventId === editingEvent.id && s.gender === 'F' && s.region === 'Regional' && s.ageGroup === editingEvent.ageGroup && s.course === editingEvent.course)?.cutTimeSeconds || 0) || ''} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs font-bold text-white" />
                      <input name="stateCutF" type="text" placeholder="State (mm:ss.xx)" defaultValue={formatTime(standards.find(s => s.eventId === editingEvent.id && s.gender === 'F' && s.region === 'State' && s.ageGroup === editingEvent.ageGroup && s.course === editingEvent.course)?.cutTimeSeconds || 0) || ''} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs font-bold text-white" />
                    </div>
                  </div>
                  
                  <button type="submit" className="w-full bg-green-600 py-3 rounded-lg font-black uppercase text-[10px] hover:bg-green-500 transition-colors mt-4">Save Changes</button>
                </form>
              ) : (
                <div className="space-y-4">
                  <form onSubmit={handleAddEvent} className="bg-slate-800 p-4 rounded-xl border border-slate-700 space-y-3">
                    <p className="text-[10px] font-black uppercase text-slate-500">New Master Event</p>
                    <div className="grid grid-cols-2 gap-2">
                      <input name="name" type="text" placeholder="50 Free" required className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs font-bold text-white" />
                      <input name="distance" type="number" placeholder="Distance" required className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs font-bold text-white" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <select name="stroke" required className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs font-bold text-white">
                        {Object.values(Stroke).map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <select name="course" required className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs font-bold text-white">
                        {Object.values(Course).map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <select name="ageGroup" required className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-xs font-bold text-white">
                      <option value="10U">10U</option>
                      <option value="11-12">11-12</option>
                      <option value="13-14">13-14</option>
                      <option value="15-16">15-16</option>
                      <option value="17-18">17-18</option>
                    </select>
                    <button type="submit" className="w-full bg-blue-600 py-3 rounded-lg font-black uppercase text-[10px] flex items-center justify-center"><Plus className="w-4 h-4 mr-2" /> Create</button>
                  </form>
                  <div className="max-h-64 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                    {events.map(ev => <div key={ev.id} className="bg-slate-800/50 p-4 rounded-lg border border-slate-700 flex justify-between items-center group"><div><p className="font-bold text-xs">{ev.name}</p><p className="text-[9px] text-slate-500 font-bold uppercase">{ev.stroke} • {ev.ageGroup}</p></div><div className="flex space-x-2"><button onClick={() => setEditingEvent(ev)} className="text-slate-500 hover:text-blue-400 p-1"><Edit3 className="w-4 h-4" /></button></div></div>)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Render shared team view (public, no login required)
  const renderSharedView = () => {
    if (!sharedTeamData) {
      return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-slate-600">Loading shared team...</p>
          </div>
        </div>
      );
    }

    const sharedAthletes = sharedTeamData.athletes || [];
    const sharedTimes = sharedTeamData.times || [];
    const sharedEvents = sharedTeamData.events || [];
    const sharedStandards = sharedTeamData.standards || [];

    const getBestTime = (athleteId: string, eventId: string) => {
      const athleteTimes = sharedTimes.filter((t: any) => t.athleteId === athleteId && t.eventId === eventId && !t.isDQ);
      if (athleteTimes.length === 0) return null;
      return Math.min(...athleteTimes.map((t: any) => t.timeSeconds));
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-blue-50 p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="bg-gradient-to-br from-purple-600 to-blue-600 p-3 rounded-xl">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-black text-slate-900 uppercase italic">{sharedTeamData.shareName}</h1>
                  <p className="text-sm text-slate-500 flex items-center space-x-2">
                    <Eye className="w-4 h-4" />
                    <span>{sharedTeamData.viewCount} views</span>
                    <span>•</span>
                    <span>{sharedAthletes.length} swimmers</span>
                    <span>•</span>
                    <span>{sharedTimes.length} total times</span>
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-black text-slate-400 uppercase">Powered by</p>
                <p className="text-lg font-black italic">SwimQual<span className="text-blue-600">.app</span></p>
              </div>
            </div>
          </div>

          {/* Search/Filter Bar */}
          <div data-testid="shared-search-bar" className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                data-testid="shared-search-input"
                type="text"
                value={sharedSearchQuery}
                onChange={e => setSharedSearchQuery(e.target.value)}
                placeholder="Search swimmers or events..."
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-bold text-slate-800 outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-slate-400"
              />
              {sharedSearchQuery && (
                <button onClick={() => setSharedSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Swimmers Grid */}
          <div className="space-y-6">
            {sharedAthletes.filter((athlete: any) => {
              if (!sharedSearchQuery) return true;
              const q = sharedSearchQuery.toLowerCase();
              // Match swimmer name
              if (athlete.name?.toLowerCase().includes(q)) return true;
              // Match event names for this swimmer
              const athleteEventIds = [...new Set(sharedTimes.filter((t: any) => t.athleteId === athlete.id).map((t: any) => t.eventId))];
              const hasMatchingEvent = sharedEvents.some((e: any) => 
                athleteEventIds.includes(e.id) && (e.name?.toLowerCase().includes(q) || e.stroke?.toLowerCase().includes(q))
              );
              return hasMatchingEvent;
            }).map((athlete: any) => {
              // Get all events that either: are selected by the athlete OR have times recorded
              const athleteTimes = sharedTimes.filter((t: any) => t.athleteId === athlete.id);
              const eventIdsWithTimes = [...new Set(athleteTimes.map((t: any) => t.eventId))];
              const selectedIds = athlete.selectedEventIds || [];
              const allRelevantEventIds = [...new Set([...selectedIds, ...eventIdsWithTimes])];
              let athleteEvents = sharedEvents.filter((e: any) => 
                allRelevantEventIds.includes(e.id)
              );
              
              // If searching by event name (not swimmer name), filter events too
              if (sharedSearchQuery && !athlete.name?.toLowerCase().includes(sharedSearchQuery.toLowerCase())) {
                const q = sharedSearchQuery.toLowerCase();
                athleteEvents = athleteEvents.filter((e: any) => 
                  e.name?.toLowerCase().includes(q) || e.stroke?.toLowerCase().includes(q)
                );
              }

              return (
                <div key={athlete.id} className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                  {/* Athlete Header */}
                  <div className="bg-gradient-to-r from-slate-800 to-slate-700 p-4 text-white">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                          <UserIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-black text-lg uppercase italic">{athlete.name}</h3>
                          <p className="text-sm text-slate-300 font-bold">{athlete.ageGroup} • {athlete.gender === 'M' ? 'Boys' : 'Girls'}</p>
                        </div>
                      </div>
                      <div className="text-right text-sm">
                        <p className="text-slate-400">{athleteEvents.length} events</p>
                        <p className="text-slate-400">{athleteTimes.length} times recorded</p>
                      </div>
                    </div>
                  </div>

                  {/* Events & All Times */}
                  <div className="p-4 space-y-4">
                    {athleteEvents.length === 0 && athleteTimes.length === 0 ? (
                      <p className="text-center text-slate-400 text-sm py-4">No times recorded yet</p>
                    ) : (
                      athleteEvents.map((event: any) => {
                        const eventTimes = sharedTimes
                          .filter((t: any) => t.athleteId === athlete.id && t.eventId === event.id)
                          .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
                        const bestTime = getBestTime(athlete.id, event.id);
                        const eventStandards = sharedStandards.filter((s: any) => 
                          s.eventId === event.id && 
                          s.ageGroup === athlete.ageGroup && 
                          s.gender === athlete.gender
                        );
                        const stateStandard = eventStandards.find((s: any) => s.region === 'State');
                        const regionalStandard = eventStandards.find((s: any) => s.region === 'Regional');
                        
                        const isStateQualified = bestTime && stateStandard && bestTime <= stateStandard.cutTimeSeconds;
                        const isRegionalQualified = bestTime && regionalStandard && bestTime <= regionalStandard.cutTimeSeconds;

                        return (
                          <div key={event.id} className="border border-slate-100 rounded-xl overflow-hidden">
                            {/* Event Header */}
                            <div className="flex items-center justify-between p-3 bg-slate-50">
                              <div className="flex items-center space-x-3">
                                <div className="bg-blue-600 p-2 rounded-lg">
                                  <Activity className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                  <div className="flex items-center space-x-2">
                                    <p className="font-black text-sm text-slate-800">{event.name}</p>
                                    <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${event.course === 'SCY' ? 'bg-blue-100 text-blue-600' : event.course === 'LCM' ? 'bg-amber-100 text-amber-600' : 'bg-purple-100 text-purple-600'}`}>{event.course || 'SCY'}</span>
                                  </div>
                                  <div className="flex items-center space-x-2 mt-1">
                                    {isStateQualified && (
                                      <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded bg-amber-100 text-amber-700">STATE QUALIFIED</span>
                                    )}
                                    {!isStateQualified && isRegionalQualified && (
                                      <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded bg-green-100 text-green-700">REGIONAL QUALIFIED</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-[10px] font-black text-slate-400 uppercase">Best Time</p>
                                {bestTime ? (
                                  <p className="font-black text-xl text-blue-600">{formatTime(bestTime)}</p>
                                ) : (
                                  <p className="text-slate-300 text-sm">No time</p>
                                )}
                              </div>
                            </div>
                            
                            {/* All Times for this Event */}
                            {eventTimes.length > 0 && (
                              <div className="p-3 bg-white">
                                <p className="text-[10px] font-black text-slate-400 uppercase mb-2">All Times ({eventTimes.length})</p>
                                <div className="space-y-2 max-h-40 overflow-y-auto">
                                  {eventTimes.map((time: any, idx: number) => (
                                    <div key={time.id || idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                                      <div className="flex items-center space-x-3">
                                        <span className={`font-black ${time.isDQ ? 'text-red-500' : 'text-blue-600'}`}>
                                          {time.isDQ ? 'DQ' : formatTime(time.timeSeconds)}
                                        </span>
                                        {time.timeSeconds === bestTime && !time.isDQ && (
                                          <span className="text-[8px] font-black uppercase px-1.5 py-0.5 rounded bg-blue-100 text-blue-600">PR</span>
                                        )}
                                      </div>
                                      <div className="text-right text-xs text-slate-500">
                                        <span>{new Date(time.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                        {time.meetName && <span className="ml-2 text-slate-400">• {time.meetName}</span>}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Standards Info */}
                            {(stateStandard || regionalStandard) && (
                              <div className="px-3 py-2 bg-slate-50 border-t border-slate-100 flex items-center space-x-4 text-[10px]">
                                {regionalStandard && (
                                  <span className="text-slate-500">
                                    <span className="font-black uppercase">Regional:</span> {formatTime(regionalStandard.cutTimeSeconds)}
                                  </span>
                                )}
                                {stateStandard && (
                                  <span className="text-slate-500">
                                    <span className="font-black uppercase">State:</span> {formatTime(stateStandard.cutTimeSeconds)}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="mt-8 text-center">
            <a 
              href="/" 
              className="inline-flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-black uppercase text-sm hover:bg-blue-500 transition-all"
            >
              <Trophy className="w-5 h-5" />
              <span>Track Your Swimmers with SwimQual.app</span>
            </a>
          </div>
        </div>
      </div>
    );
  };

  const getContent = () => {
    switch (currentScreen) {
      case 'dashboard': return renderDashboard();
      case 'progress': return renderProgress();
      case 'event-detail': return selectedEventId && currentAthlete ? <div className="space-y-6 pb-10"><button onClick={() => setCurrentScreen('dashboard')} className="flex items-center text-slate-500 font-bold text-xs uppercase tracking-widest hover:text-sky-400 transition-colors"><ChevronLeft className="w-4 h-4 mr-1" /> Back</button><div className="glass-card p-6 rounded-2xl"><div className="flex justify-between items-start mb-8"><div><h3 className="font-display text-3xl font-black text-white uppercase">{events.find(e => e.id === selectedEventId)?.name}</h3><div className="flex items-center space-x-2 mt-1"><p className="text-sm font-bold text-slate-500 uppercase">{events.find(e => e.id === selectedEventId)?.stroke}</p></div></div><button onClick={() => setCurrentScreen('add-time')} className="bg-sky-500 hover:bg-sky-400 text-white p-3 rounded-xl shadow-lg shadow-sky-500/30 transition-all btn-press"><Plus className="w-6 h-6" /></button></div><div className="h-72 w-full"><ResponsiveContainer width="100%" height="100%"><RechartsLine data={times.filter(t => t.eventId === selectedEventId && t.athleteId === currentAthlete.id && !t.isDQ).sort((a,b) => new Date(a.date).getTime() - new Date(b.date).getTime()).map(t => ({ date: new Date(t.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }), time: t.timeSeconds }))}><CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1e293b" /><XAxis dataKey="date" tick={{fontSize: 9, fill: '#64748b'}} axisLine={false} tickLine={false} /><YAxis hide domain={['auto', 'auto']} /><Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#fff' }} formatter={(v: number) => [formatTime(v), 'Result']} /><Line type="monotone" dataKey="time" stroke="#0ea5e9" strokeWidth={4} dot={{r: 5, fill: '#0ea5e9'}} /></RechartsLine></ResponsiveContainer></div></div><div className="glass-card p-6 rounded-2xl"><h4 className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-4 flex items-center"><History className="w-4 h-4 mr-2" /> Time History</h4><div className="space-y-3">{times.filter(t => t.eventId === selectedEventId && t.athleteId === currentAthlete.id).sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(t => <div key={t.id} className="flex justify-between items-center p-4 bg-slate-900/60 rounded-xl border border-white/5"><div><p className={`font-display text-2xl font-black ${t.isDQ ? 'text-red-400' : 'text-sky-400'}`}>{t.isDQ ? 'DQ' : formatTime(t.timeSeconds)}</p><div className="flex items-center space-x-2 mt-1"><Calendar className="w-3 h-3 text-slate-500" /><span className="text-xs text-slate-500 font-bold">{new Date(t.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>{t.meetName && <><span className="w-1 h-1 bg-slate-600 rounded-full"></span><span className="text-xs text-slate-400 font-bold">{t.meetName}</span></>}</div></div><button onClick={() => setEditingTimeEntry(t)} className="text-slate-500 hover:text-sky-400 p-2 rounded-lg hover:bg-sky-500/10 transition-all"><Edit3 className="w-4 h-4" /></button><button onClick={() => handleDeleteTime(t.id)} className="text-slate-500 hover:text-red-400 p-2 rounded-lg hover:bg-red-500/10 transition-all"><Trash2 className="w-4 h-4" /></button></div>)}{times.filter(t => t.eventId === selectedEventId && t.athleteId === currentAthlete.id).length === 0 && <p className="text-center text-slate-500 text-sm py-8">No times recorded yet</p>}</div></div>{editingTimeEntry && <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"><div className="glass-card rounded-2xl p-6 w-full max-w-md shadow-2xl"><div className="flex justify-between items-center mb-6"><h3 className="font-display text-xl font-bold text-white uppercase">Edit Time</h3><button onClick={() => setEditingTimeEntry(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button></div><form onSubmit={handleUpdateTime} className="space-y-4"><div className="flex items-center space-x-3 p-3 bg-red-500/10 border border-red-500/30 rounded-xl"><input name="isDQ" type="checkbox" id="editIsDQ" defaultChecked={editingTimeEntry.isDQ} className="w-5 h-5 rounded border-red-500/50 text-red-500 focus:ring-red-500 bg-slate-900" /><label htmlFor="editIsDQ" className="text-sm font-bold text-red-400 uppercase">Disqualified (DQ)</label></div><div><label className="block text-[10px] font-bold text-slate-500 uppercase mb-2 tracking-widest">Time (mm:ss.xx)</label><input name="time" type="text" defaultValue={editingTimeEntry.isDQ ? '' : formatTime(editingTimeEntry.timeSeconds)} className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3 font-bold text-white outline-none focus:border-sky-500/50 transition-all" /></div><div><label className="block text-[10px] font-bold text-slate-500 uppercase mb-2 tracking-widest">Meet Date</label><input name="date" type="date" defaultValue={editingTimeEntry.date} className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3 font-bold text-white outline-none focus:border-sky-500/50 transition-all" /></div><div><label className="block text-[10px] font-bold text-slate-500 uppercase mb-2 tracking-widest">Meet Name (optional)</label><input name="meetName" type="text" defaultValue={editingTimeEntry.meetName || ''} placeholder="e.g. Regional Championships" className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3 font-bold text-white outline-none focus:border-sky-500/50 transition-all placeholder:text-slate-600" /></div><div className="flex space-x-3 pt-2"><button type="button" onClick={() => handleDeleteTime(editingTimeEntry.id)} className="bg-red-500/10 text-red-400 py-3 px-4 rounded-xl font-bold uppercase text-xs hover:bg-red-500/20 transition-all"><Trash2 className="w-4 h-4" /></button><button type="button" onClick={() => setEditingTimeEntry(null)} className="flex-1 bg-slate-800 text-slate-300 py-3 rounded-xl font-bold uppercase text-xs hover:bg-slate-700">Cancel</button><button type="submit" className="flex-1 bg-sky-500 text-white py-3 rounded-xl font-bold uppercase text-xs hover:bg-sky-400">Save Changes</button></div></form></div></div>}{deletingTimeId && <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"><div className="glass-card rounded-2xl p-6 w-full max-w-sm shadow-2xl text-center"><div className="bg-red-500/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"><Trash2 className="w-8 h-8 text-red-400" /></div><h3 className="font-display text-xl font-bold text-white uppercase mb-2">Delete Time?</h3><p className="text-slate-400 text-sm mb-6">This action cannot be undone.</p><div className="flex space-x-3"><button onClick={() => setDeletingTimeId(null)} className="flex-1 bg-slate-800 text-slate-300 py-3 rounded-xl font-bold uppercase text-xs hover:bg-slate-700">Cancel</button><button onClick={confirmDeleteTime} className="flex-1 bg-red-500 text-white py-3 rounded-xl font-bold uppercase text-xs hover:bg-red-400">Delete</button></div></div></div>}</div> : null;
      case 'manage-swimmer-events': return currentAthlete ? <div className="space-y-6"><button onClick={() => setCurrentScreen('dashboard')} className="flex items-center text-slate-500 font-bold text-xs uppercase tracking-widest hover:text-sky-400 transition-colors"><ChevronLeft className="w-4 h-4 mr-1" /> Back</button><div className="glass-card p-6 rounded-2xl"><h3 className="font-display text-xl font-bold text-white uppercase mb-2">Track Your Events</h3><p className="text-xs text-slate-500 font-bold uppercase tracking-widest mb-6">Age Group: {currentAthlete.ageGroup} • {currentAthlete.selectedEventIds.length} tracked</p><div className="space-y-6">{(['SCY', 'LCM', 'SCM'] as const).map(course => {
        const courseEvents = events.filter(e => e.ageGroup === currentAthlete.ageGroup && e.course === course);
        if (courseEvents.length === 0) return null;
        
        // Deduplicate by normalized name - keep the one that has times or is selected
        const normalizeEventName = (name: string) => {
          let n = name.replace(/^(Boys|Girls)\s*(12&U|10&U|11-12|13-14|15-16|17-18)?\s*/i, '');
          n = n.replace(/\s*(Yard|Meter|Meters|Y|M)\s*/i, ' ').trim();
          n = n.replace(/\s+/g, ' ');
          return n;
        };
        
        const deduped = new Map<string, typeof courseEvents[0]>();
        for (const e of courseEvents) {
          const normName = normalizeEventName(e.name);
          const existing = deduped.get(normName);
          if (!existing) {
            deduped.set(normName, e);
          } else {
            // Keep the one that's selected, or has times, or has standards
            const isSelected = currentAthlete.selectedEventIds.includes(e.id);
            const existingSelected = currentAthlete.selectedEventIds.includes(existing.id);
            const hasTimes = times.some(t => t.athleteId === currentAthlete.id && t.eventId === e.id);
            const existingHasTimes = times.some(t => t.athleteId === currentAthlete.id && t.eventId === existing.id);
            if ((isSelected && !existingSelected) || (hasTimes && !existingHasTimes)) {
              deduped.set(normName, e);
            }
          }
        }
        
        const uniqueEvents = Array.from(deduped.values()).sort((a, b) => {
          const na = normalizeEventName(a.name);
          const nb = normalizeEventName(b.name);
          return na.localeCompare(nb);
        });
        
        return <div key={course}><div className="flex items-center justify-between mb-3"><div className="flex items-center space-x-2"><span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded ${course === 'SCY' ? 'bg-sky-500/20 text-sky-400' : course === 'LCM' ? 'bg-amber-500/20 text-amber-400' : 'bg-purple-500/20 text-purple-400'}`}>{course}</span><span className="text-[10px] text-slate-600 font-bold uppercase">{course === 'SCY' ? 'Short Course Yards' : course === 'LCM' ? 'Long Course Meters' : 'Short Course Meters'}</span></div><span className="text-[9px] text-slate-600 font-bold">{uniqueEvents.length} events</span></div><div className="space-y-2">{uniqueEvents.map(e => {
          const isSelected = currentAthlete.selectedEventIds.includes(e.id);
          const eventStds = standards.filter(s => s.eventId === e.id && s.gender === currentAthlete.gender);
          const hasRegional = eventStds.some(s => s.region === 'Regional');
          const hasState = eventStds.some(s => s.region === 'State');
          const verScore = eventStds.find(s => s.verificationScore)?.verificationScore;
          const verConf = eventStds.find(s => s.verificationConfidence)?.verificationConfidence;
          const hasTimes = times.some(t => t.athleteId === currentAthlete.id && t.eventId === e.id);
          const bestTime = times.filter(t => t.athleteId === currentAthlete.id && t.eventId === e.id).sort((a, b) => a.timeSeconds - b.timeSeconds)[0];
          const displayName = normalizeEventName(e.name);
          
          return <div key={e.id} data-testid={`event-toggle-${e.id}`} onClick={() => handleToggleEventSelection(e.id)} className={`p-3 rounded-xl border flex items-center justify-between cursor-pointer transition-all btn-press ${isSelected ? 'border-sky-500/50 bg-sky-500/10' : 'border-white/5 bg-slate-900/60 hover:border-sky-500/30'}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <p className="font-bold text-sm text-white">{displayName}</p>
                {hasTimes && <span className="text-[8px] font-bold bg-green-500/10 text-green-400 px-1.5 py-0.5 rounded">PR: {formatTime(bestTime.timeSeconds)}</span>}
              </div>
              <div className="flex items-center space-x-2 mt-1">
                {hasRegional && <span className="text-[8px] text-emerald-500 font-bold">REG</span>}
                {hasState && <span className="text-[8px] text-amber-500 font-bold">STATE</span>}
                {verScore ? (
                  <span className={`text-[8px] font-bold ${verConf === 'high' ? 'text-green-400' : verConf === 'medium' ? 'text-amber-400' : 'text-slate-500'}`}>{verScore}</span>
                ) : (hasRegional || hasState) ? (
                  <span className="text-[8px] text-slate-600 font-bold">UNVERIFIED</span>
                ) : null}
                {!hasRegional && !hasState && <span className="text-[8px] text-slate-600">No cuts</span>}
              </div>
            </div>
            {isSelected ? <CheckSquare className="w-5 h-5 text-sky-400 flex-shrink-0" /> : <Square className="w-5 h-5 text-slate-600 flex-shrink-0" />}
          </div>;
        })}</div></div>;
      })}</div></div></div> : null;
      case 'focus': return renderFocusScreen();
      case 'roster': return <div className="space-y-4">
        {/* Share Team Button */}
        {(currentUser?.role === Role.COACH || currentUser?.role === Role.ADMIN || currentUser?.role === Role.PARENT) && (
          <button 
            onClick={handleCreateShareLink} 
            disabled={isCreatingShare}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-600 text-white p-4 rounded-2xl font-bold uppercase text-xs flex items-center justify-center space-x-2 hover:from-purple-400 hover:to-pink-500 transition-all shadow-lg shadow-purple-500/30 btn-press"
            data-testid="share-team-btn"
          >
            <Share2 className="w-5 h-5" />
            <span>{isCreatingShare ? 'Creating Link...' : 'Share Team'}</span>
          </button>
        )}
        {/* Invite Family & Coaches */}
        <div className="glass-card p-5 rounded-2xl space-y-4">
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-3 rounded-xl shadow-lg shadow-emerald-500/30"><Users className="w-5 h-5 text-white" /></div>
            <div>
              <h3 className="font-display text-lg font-bold text-white uppercase leading-none">Invite Family & Coaches</h3>
              <p className="text-[10px] text-slate-500 font-bold uppercase mt-1 tracking-widest">They can add & edit times</p>
            </div>
          </div>
          {inviteCode ? (
            <div className="flex items-center justify-between p-4 bg-slate-900/60 border border-emerald-500/30 rounded-xl">
              <div>
                <p className="text-[9px] font-bold text-emerald-400 uppercase tracking-widest mb-1">Your Team Code</p>
                <p className="font-mono text-2xl font-bold text-white tracking-[0.3em]">{inviteCode}</p>
              </div>
              <button onClick={handleCopyInviteCode} className="px-4 py-2 rounded-xl bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 font-bold text-xs uppercase flex items-center space-x-2 hover:bg-emerald-500/30 transition-all btn-press">
                {inviteCopied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                <span>{inviteCopied ? 'Copied!' : 'Copy'}</span>
              </button>
            </div>
          ) : (
            <button onClick={handleGetInviteCode} disabled={isLoadingInvite} className="w-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 p-4 rounded-xl font-bold uppercase text-xs flex items-center justify-center space-x-2 hover:bg-emerald-500/30 transition-all btn-press disabled:opacity-40">
              <Key className="w-4 h-4" />
              <span>{isLoadingInvite ? 'Generating...' : 'Generate Team Code'}</span>
            </button>
          )}
          <div className="border-t border-white/5 pt-4">
            <p className="text-[10px] font-bold text-slate-500 uppercase mb-2 tracking-widest">Have a code? Join another team</p>
            <form onSubmit={handleJoinTeam} className="flex space-x-2">
              <input
                name="joinCode"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value)}
                placeholder="Enter invite code"
                className="flex-1 bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3 text-white font-mono font-bold uppercase outline-none focus:border-sky-500/50 transition-all placeholder:text-slate-600 placeholder:font-sans placeholder:font-normal placeholder:normal-case"
              />
              <button type="submit" disabled={isJoiningTeam || !joinCode.trim()} className="px-5 py-3 rounded-xl bg-sky-500 hover:bg-sky-400 text-white font-bold uppercase text-xs transition-all btn-press disabled:opacity-40">
                {isJoiningTeam ? 'Joining...' : 'Join'}
              </button>
            </form>
            {teamJoinError && <p className="mt-2 text-red-400 text-xs font-bold bg-red-500/10 px-3 py-2 rounded-lg flex items-center space-x-2"><AlertCircle className="w-3 h-3 flex-shrink-0" /><span>{teamJoinError}</span></p>}
            {teamJoinSuccess && <p className="mt-2 text-emerald-400 text-xs font-bold bg-emerald-500/10 px-3 py-2 rounded-lg flex items-center space-x-2"><CheckCircle2 className="w-3 h-3 flex-shrink-0" /><span>{teamJoinSuccess}</span></p>}
          </div>
        </div>
        {(currentUser?.role === Role.PARENT || currentUser?.role === Role.COACH || currentUser?.role === Role.ADMIN) && <button onClick={() => setCurrentScreen('add-athlete')} className="w-full bg-sky-500 hover:bg-sky-400 text-white p-4 rounded-2xl font-bold uppercase text-xs flex items-center justify-center space-x-2 transition-all shadow-lg shadow-sky-500/30 btn-press"><Plus className="w-5 h-5" /><span>Add Swimmer</span></button>}{visibleAthletes.length === 0 ? <div className="text-center py-16 glass-card rounded-2xl"><Users className="w-12 h-12 text-slate-600 mx-auto mb-4" /><p className="text-slate-400 font-bold mb-2 uppercase text-xs">No swimmers yet</p><p className="text-slate-500 text-sm">Click the button above to add your first swimmer</p></div> : visibleAthletes.map(a => <div key={a.id} onClick={() => { setSelectedAthleteId(a.id); setCurrentScreen('dashboard'); setActiveTab('dashboard'); }} className={`glass-card p-5 rounded-2xl flex justify-between items-center cursor-pointer hover:border-sky-500/30 transition-all group ${selectedAthleteId === a.id ? 'border-sky-500/50' : ''}`}><div className="flex items-center space-x-4"><div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-lg shadow-sky-500/30"><span className="font-display text-lg font-bold text-white">{a.name.charAt(0)}</span></div><div><p className="font-display text-lg font-bold text-white uppercase leading-none mb-1">{a.name}</p><p className="text-[10px] text-slate-500 font-bold uppercase">{a.ageGroup} • {a.gender === 'M' ? 'Male' : 'Female'}</p>{(() => { const mv = getNextAgeGroupChange(a.dob); if (!mv) return null; return <p className="text-[9px] font-bold text-amber-400 uppercase tracking-widest mt-0.5">→ {mv.group} on {mv.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>; })()}</div></div><ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-sky-400 transition-colors" /></div>)}</div>;
      case 'admin': return renderAdmin();
      case 'add-time': return selectedEventId && currentAthlete ? <div className="max-w-md mx-auto space-y-6"><button onClick={() => setCurrentScreen('event-detail')} className="flex items-center text-slate-500 font-bold text-xs uppercase tracking-widest hover:text-sky-400 transition-colors"><ChevronLeft className="w-4 h-4 mr-1" /> Back</button><div className="glass-card p-8 rounded-2xl space-y-6"><div className="border-b border-white/5 pb-6 mb-2"><div className="flex items-center space-x-3 mb-2"><div className="bg-gradient-to-br from-sky-500 to-blue-600 p-3 rounded-xl shadow-lg shadow-sky-500/30"><Clock className="w-5 h-5 text-white" /></div><h3 className="font-display text-2xl font-bold text-white uppercase">Add Result</h3></div><div className="flex items-center space-x-2 mt-1"><span className="font-display font-bold text-white text-sm uppercase">{events.find(e => e.id === selectedEventId)?.name}</span><span className="w-1 h-1 bg-slate-600 rounded-full"></span><span className="text-xs font-bold text-sky-400 uppercase tracking-widest">{currentAthlete.name}</span></div></div><form onSubmit={handleAddTime} className="space-y-6"><div className="flex items-center space-x-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl"><input name="isDQ" type="checkbox" id="isDQ" className="w-5 h-5 rounded border-red-500/50 text-red-500 focus:ring-red-500 bg-slate-900" /><label htmlFor="isDQ" className="text-sm font-bold text-red-400 uppercase">Disqualified (DQ)</label></div><div><label className="block text-[10px] font-bold text-slate-500 uppercase mb-2 tracking-widest">Time (mm:ss.xx)</label><input name="time" type="text" placeholder="1:05.42" className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-4 font-bold text-white outline-none focus:border-sky-500/50 transition-all placeholder:text-slate-600" /></div><div><label className="block text-[10px] font-bold text-slate-500 uppercase mb-2 tracking-widest">Meet Date</label><input name="date" type="date" defaultValue={new Date().toISOString().split('T')[0]} className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-4 font-bold text-white outline-none focus:border-sky-500/50 transition-all" /></div><div><label className="block text-[10px] font-bold text-slate-500 uppercase mb-2 tracking-widest">Meet Name (optional)</label><input name="meetName" type="text" placeholder="e.g. Regional Championships" className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-4 font-bold text-white outline-none focus:border-sky-500/50 transition-all placeholder:text-slate-600" /></div><button type="submit" className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white py-4 rounded-xl font-bold uppercase tracking-widest shadow-xl shadow-sky-500/30 hover:shadow-sky-500/40 transition-all btn-press">Save Record</button></form></div></div> : null;
      case 'add-athlete': return <div className="max-w-md mx-auto"><button onClick={() => setCurrentScreen('roster')} className="flex items-center text-slate-500 font-bold text-xs uppercase tracking-widest hover:text-sky-400 mb-6 transition-colors"><ChevronLeft className="w-4 h-4 mr-1" /> Back</button><div className="glass-card p-8 rounded-2xl space-y-6"><h3 className="font-display text-2xl font-bold text-white uppercase">New Swimmer</h3><form onSubmit={handleAddAthlete} className="space-y-6"><div><label className="block text-[10px] font-bold text-slate-500 uppercase mb-2 tracking-widest">Name</label><input name="name" type="text" required className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-4 font-bold text-white outline-none focus:border-sky-500/50 transition-all" /></div><div><label className="block text-[10px] font-bold text-slate-500 uppercase mb-2 tracking-widest">Birth Date</label><input name="dob" type="date" required className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-4 font-bold text-white outline-none focus:border-sky-500/50 transition-all" /></div><div><label className="block text-[10px] font-bold text-slate-500 uppercase mb-2 tracking-widest">Gender</label><select name="gender" required className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-4 font-bold text-white outline-none focus:border-sky-500/50 transition-all"><option value="M">Male</option><option value="F">Female</option></select></div><button type="submit" className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white py-4 rounded-xl font-bold uppercase tracking-widest shadow-xl shadow-sky-500/30 hover:shadow-sky-500/40 transition-all btn-press">Add Athlete</button></form></div></div>;
      case 'scan-times': return renderScanTimesScreen();
      case 'shared-view': return renderSharedView();
      default: return renderDashboard();
    }
  };

  if (currentScreen === 'login') return (
    <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center p-6">
      <div className="w-full max-w-md text-center">
        <div className="flex flex-col items-center mb-8 animate-slide-up">
          <div className="w-20 h-20 bg-gradient-to-br from-sky-500 to-blue-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-sky-500/30 float glow-pulse">
            <Trophy className="text-white w-10 h-10" />
          </div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tight text-white">
            SwimQual
          </h1>
          <p className="text-slate-500 text-sm mt-2">Track. Qualify. Dominate.</p>
        </div>
        <div className="glass-card rounded-3xl p-8 animate-slide-up" style={{ animationDelay: '0.2s' }}>
          <form onSubmit={handleLoginSubmit} className="space-y-5">
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input data-testid="login-email-input" name="email" type="email" placeholder="alex@team.com" required className="w-full bg-slate-900/60 border border-white/10 rounded-xl pl-12 pr-4 py-4 text-white font-medium outline-none focus:border-sky-500/50 focus:shadow-lg focus:shadow-sky-500/10 transition-all placeholder:text-slate-600" />
            </div>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input data-testid="login-password-input" name="password" type="password" placeholder="••••••••" required className="w-full bg-slate-900/60 border border-white/10 rounded-xl pl-12 pr-4 py-4 text-white font-medium outline-none focus:border-sky-500/50 focus:shadow-lg focus:shadow-sky-500/10 transition-all placeholder:text-slate-600" />
            </div>
            {loginError && <p data-testid="login-error" className="text-red-400 text-xs font-bold bg-red-500/10 px-4 py-2 rounded-lg animate-scale-in">{loginError}</p>}
            <button data-testid="login-submit-button" type="submit" className="w-full py-4 rounded-xl font-bold uppercase bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-xl shadow-sky-500/30 hover:shadow-sky-500/40 hover:scale-[1.02] transition-all btn-press ripple">
              Log In
            </button>
          </form>
          <div className="mt-6 pt-6 border-t border-white/5">
            <p className="text-sm text-slate-400">Don't have an account? <button data-testid="go-to-register-button" onClick={() => { setCurrentScreen('register'); setLoginError(null); }} className="text-sky-400 font-bold hover:text-sky-300 transition-colors">Register</button></p>
          </div>
          <div className="mt-6 pt-6 border-t border-white/5">
            <p className="text-[10px] font-bold text-slate-500 uppercase mb-4 tracking-widest text-center">Demo Accounts</p>
            <div className="grid grid-cols-2 gap-2">
              {['alex@team.com', 'maria@parent.com', 'sarah@team.com', 'admin@swim.com'].map((e, i) => (
                <button 
                  key={e} 
                  data-testid={`demo-login-${e.split('@')[0]}`}
                  onClick={() => quickLogin(e)} 
                  className="bg-slate-900/60 hover:bg-sky-500/20 border border-white/5 hover:border-sky-500/30 rounded-xl p-3 text-[10px] font-bold uppercase text-slate-400 hover:text-sky-400 truncate transition-all btn-press animate-fade-in"
                  style={{ animationDelay: `${0.3 + i * 0.1}s` }}
                >
                  {e.split('@')[0]}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (currentScreen === 'register') return (
    <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center p-6">
      <div className="w-full max-w-md text-center">
        <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-emerald-500/30">
          <UserPlus className="text-white w-10 h-10" />
        </div>
        <h1 className="font-display text-3xl font-black uppercase tracking-tight text-white mb-2">Create Account</h1>
        <p className="text-slate-500 text-sm mb-8">Join the SwimQual community</p>
        <div className="glass-card rounded-3xl p-8">
          <form onSubmit={handleRegisterSubmit} className="space-y-5">
            <input data-testid="register-name-input" name="name" type="text" placeholder="Your Name" required className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-4 text-white font-medium outline-none focus:border-sky-500/50 transition-all placeholder:text-slate-600" />
            <input data-testid="register-email-input" name="email" type="email" placeholder="email@example.com" required className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-4 text-white font-medium outline-none focus:border-sky-500/50 transition-all placeholder:text-slate-600" />
            <input data-testid="register-password-input" name="password" type="password" placeholder="Password" required minLength={6} className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-4 text-white font-medium outline-none focus:border-sky-500/50 transition-all placeholder:text-slate-600" />
            <input name="inviteCode" type="text" placeholder="Invite Code (optional — join a family team)" className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-4 text-white font-mono font-bold uppercase outline-none focus:border-emerald-500/50 transition-all placeholder:text-slate-600 placeholder:font-sans placeholder:font-normal placeholder:normal-case" />
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-2 tracking-widest text-left">I am a...</label>
              <select data-testid="register-role-select" name="role" required className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-4 text-white font-medium outline-none focus:border-sky-500/50 transition-all">
                <option value={Role.SWIMMER}>Swimmer</option>
                <option value={Role.PARENT}>Parent</option>
                <option value={Role.COACH}>Coach</option>
              </select>
            </div>
            {registerError && <p data-testid="register-error" className="text-red-400 text-xs font-bold bg-red-500/10 px-4 py-2 rounded-lg">{registerError}</p>}
            <button data-testid="register-submit-button" type="submit" className="w-full py-4 rounded-xl font-bold uppercase bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-xl shadow-emerald-500/30 hover:shadow-emerald-500/40 transition-all btn-press">
              Create Account
            </button>
          </form>
          <div className="mt-6 pt-6 border-t border-white/5">
            <p className="text-sm text-slate-400">Already have an account? <button data-testid="go-to-login-button" onClick={() => { setCurrentScreen('login'); setRegisterError(null); }} className="text-sky-400 font-bold hover:text-sky-300">Log In</button></p>
          </div>
        </div>
      </div>
    </div>
  );

  // If viewing shared team, render that without requiring login
  if (currentScreen === 'shared-view') {
    return renderSharedView();
  }

  return (
    <Layout 
      activeTab={activeTab} 
      setActiveTab={handleTabChange} 
      title={activeTab === 'dashboard' ? 'Season Best' : activeTab === 'progress' ? 'Progress' : activeTab === 'roster' ? 'Team Profile' : activeTab === 'focus' ? 'Performance' : 'Admin Tools'} 
      user={currentUser!} 
      onLogout={handleLogout}
    >
      {getContent()}
      
      {/* Share Modal */}
      {showShareModal && shareLink && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center space-x-3">
                <div className="bg-gradient-to-br from-purple-600 to-blue-600 p-2 rounded-xl">
                  <Share2 className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-black text-slate-900 italic uppercase">Share Team</h3>
              </div>
              <button onClick={() => setShowShareModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-sm text-slate-500 mb-4">Anyone with this link can view your team's swimmers and their times (read-only).</p>
            
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 mb-4">
              <div className="flex items-center space-x-2">
                <Link className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <input 
                  type="text" 
                  value={shareLink} 
                  readOnly 
                  className="flex-1 bg-transparent text-sm font-mono text-slate-700 outline-none truncate"
                />
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button 
                onClick={handleCopyShareLink}
                className={`flex-1 py-3 rounded-xl font-black uppercase text-xs flex items-center justify-center space-x-2 transition-all ${
                  copiedLink 
                    ? 'bg-green-600 text-white' 
                    : 'bg-blue-600 text-white hover:bg-blue-500'
                }`}
              >
                {copiedLink ? (
                  <><CheckCircle2 className="w-4 h-4" /><span>Copied!</span></>
                ) : (
                  <><Copy className="w-4 h-4" /><span>Copy Link</span></>
                )}
              </button>
              <a 
                href={`mailto:?subject=Check out our swim team!&body=View our team's times and progress: ${shareLink}`}
                className="flex-1 py-3 rounded-xl font-black uppercase text-xs flex items-center justify-center space-x-2 bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all"
              >
                <Mail className="w-4 h-4" />
                <span>Email</span>
              </a>
            </div>
            
            <div className="mt-4 pt-4 border-t border-slate-100">
              <p className="text-[10px] text-slate-400 text-center">
                Share code: <span className="font-mono font-bold">{shareCode}</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Standards Verification Modal */}
      <StandardsVerificationModal
        isOpen={showVerifyStandards}
        onClose={() => setShowVerifyStandards(false)}
      />

      {/* Apply Confirmation Modal - Compare old vs new */}
      {showApplyConfirm && researchResults.length > 0 && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowApplyConfirm(false)}>
          <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-white/10">
              <h3 className="text-lg font-black text-white uppercase">Confirm Changes</h3>
              <p className="text-xs text-slate-400 mt-1">Review what will be updated. Your swimmer times will NOT be affected.</p>
              {sourceLinks && (
                <div className="flex flex-wrap gap-2 mt-3">
                  <a href="https://www.usaswimming.org/times/time-standards" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-1 text-[9px] font-bold text-blue-400 hover:text-blue-300 bg-blue-500/10 px-2 py-1 rounded">
                    <ExternalLink className="w-3 h-3" /><span>USA Swimming</span>
                  </a>
                  {sourceLinks.lscWebsite && (
                    <a href={sourceLinks.lscWebsite} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-1 text-[9px] font-bold text-green-400 hover:text-green-300 bg-green-500/10 px-2 py-1 rounded">
                      <ExternalLink className="w-3 h-3" /><span>{sourceLinks.lscName} LSC</span>
                    </a>
                  )}
                </div>
              )}
            </div>
            <div className="p-5 overflow-y-auto max-h-[50vh] space-y-3">
              {researchResults.filter(r => r.regionalTimeStr || r.stateTimeStr).map((res, i) => {
                const existingEvent = events.find(e => e.name === res.name && e.course === (res.course || explorerFilter.course));
                const existingStds = existingEvent ? standards.filter(s => s.eventId === existingEvent.id) : [];
                const oldRegional = existingStds.find(s => s.region === 'Regional');
                const oldState = existingStds.find(s => s.region === 'State');
                const hasChange = (res.regionalTimeStr && (!oldRegional || Math.abs(oldRegional.cutTimeSeconds - parseTime(res.regionalTimeStr)) > 0.01)) ||
                                  (res.stateTimeStr && (!oldState || Math.abs(oldState.cutTimeSeconds - parseTime(res.stateTimeStr)) > 0.01));
                
                return (
                  <div key={i} className={`p-3 rounded-xl border ${hasChange ? 'border-amber-500/30 bg-amber-900/10' : 'border-green-500/20 bg-green-900/10'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-black text-white">{res.name}</span>
                      <div className="flex items-center space-x-2">
                        {res.verificationScore && (
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                            res.verificationConfidence === 'high' ? 'bg-green-500/20 text-green-400' : 
                            res.verificationConfidence === 'medium' ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-700 text-slate-400'
                          }`}>{res.verificationScore}</span>
                        )}
                        <span className={`text-[9px] font-bold ${hasChange ? 'text-amber-400' : 'text-green-400'}`}>
                          {hasChange ? 'UPDATING' : 'NO CHANGE'}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-[10px]">
                      <div>
                        <span className="text-slate-500 font-bold uppercase">Regional</span>
                        <div className="flex items-center space-x-2 mt-0.5">
                          <span className="text-slate-400">{oldRegional ? formatTime(oldRegional.cutTimeSeconds) : '—'}</span>
                          {res.regionalTimeStr && <span className="text-slate-600">→</span>}
                          {res.regionalTimeStr && <span className="text-white font-bold">{res.regionalTimeStr}</span>}
                        </div>
                      </div>
                      <div>
                        <span className="text-slate-500 font-bold uppercase">State</span>
                        <div className="flex items-center space-x-2 mt-0.5">
                          <span className="text-slate-400">{oldState ? formatTime(oldState.cutTimeSeconds) : '—'}</span>
                          {res.stateTimeStr && <span className="text-slate-600">→</span>}
                          {res.stateTimeStr && <span className="text-white font-bold">{res.stateTimeStr}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="p-5 border-t border-white/10 flex items-center justify-between">
              <p className="text-[10px] text-slate-500">
                {researchResults.filter(r => r.regionalTimeStr || r.stateTimeStr).length} events • Only cut thresholds will change, swimmer times stay intact
              </p>
              <div className="flex items-center space-x-3">
                <button onClick={() => setShowApplyConfirm(false)} className="text-slate-400 text-xs font-bold uppercase hover:text-white transition-colors px-3 py-2">
                  Cancel
                </button>
                <button 
                  data-testid="confirm-apply-btn"
                  onClick={() => { setShowApplyConfirm(false); handleApplyResults(); }} 
                  className="bg-green-600 hover:bg-green-500 text-white text-xs font-black uppercase px-4 py-2 rounded-lg flex items-center space-x-2"
                >
                  <Save className="w-3.5 h-3.5" />
                  <span>Confirm & Apply</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default App;
